import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowDown,
  Ban,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  CheckCircle2,
  ChevronDown,
  Download,
  FileText,
  Mail,
  ScrollText,
  ShieldCheck,
  Sparkles,
  Trash2,
  Upload,
  X,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import { Tooltip } from "@/components/ui/Tooltip";
import { bgTasksApi } from "@/features/bg_tasks/api/bgTasks";
import { useAuthStore } from "@/store/auth";
import { useQueryClient } from "@tanstack/react-query";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { useManagedFileOpener } from "@/hooks/useManagedFileOpener";
import { ordersApi } from "@/features/orders/api/orders";
import { ORDER_STATUS_LABELS } from "@/features/orders/types";
import { isPlainLeftClick } from "@/utils/managedFile";
import {
  assignWpwWypis,
  attachWpwMap,
  createOwner,
  finalizeWpwSequence,
  getParcelOwners,
  getWpwMapOptions,
  getWpwWypisOptions,
  persistWpwMassParcelFields,
  replaceWpwFormOdt,
  saveWpwSequence,
  updateConnection,
  updateOwner,
  uploadWpwWypis,
  type WpwMassCtx,
} from "../api";
import { OWNER_ROLE_OPTIONS } from "../types";
import type { ParcelOwner } from "../types";
import {
  useConnectionByProject,
  useParcels,
  useTaskPolling,
  useScopeConfirmPendingIds,
  useWpwMassExtraFileMutations,
  useWpwMassExtraFiles,
  useWpwMassMutations,
  useWpwMassOverview,
  useWpwMassParcelFields,
  useWpwReturnBatches,
  useWpwReturnMutations,
} from "../hooks";
import { fmtDate } from "../utils";
import { extractApiError } from "@/features/tech_calc/utils";
import type {
  DocTemplateField, WpwConsentScope, WpwConsentUnit, WpwExtraPagePlacement, WpwMailLetterPart, WpwMailPreviousLetter,
  WpwMassBlocked,
  WpwMassRow, WpwMassScopeReview, WpwReturnBatch,
} from "../types";
import {
  DOC_TEMPLATE_KIND_LABELS, WPW_ENERGA_KIND_SHORT, WPW_KIND_STATUS, wpwFormKindsFor,
  type WpwConsentStatus, type WpwEnergaFormKind,
} from "../types";
import { PageHeader, SectionCard } from "../components/ui";
import { FieldReturnReviewModal } from "../components/wpw_mass/FieldReturnReviewModal";
import { OdtEditorScreen } from "@/features/doc_editor/components/OdtEditorScreen";
import { useEditorAvailability } from "@/features/doc_editor/hooks";
import type { EditorDocKind } from "@/features/doc_editor/types";
import { KwAnalysisModal } from "../components/KwAnalysisModal";
import { WpwRequisitionModal } from "../components/WpwRequisitionModal";

// Kubełki opisu (consent_work_descriptions) potrzebne dla dobranych formularzy wiersza:
// formularz ENERGA bierze opis swojego statusu, porozumienie TD — opis wspólny. Dobór
// rodzajów przychodzi z backendu już pod operatora (OSD) projektu.
const opisStatusesOf = (row: WpwMassRow): WpwConsentStatus[] =>
  row.effective_form_kinds
    .map((k) => WPW_KIND_STATUS[k as WpwEnergaFormKind])
    .filter(Boolean);

// Odznaka OPIS: gotowe, gdy KAŻDY dobrany formularz ma niepusty opis swojego statusu.
const opisReady = (row: WpwMassRow): boolean => {
  const ss = opisStatusesOf(row);
  return ss.length > 0 && ss.every((s) => (row.parcel.consent_work_descriptions?.[s] || "").trim().length > 0);
};

const OPERATOR_LABEL: Record<string, string> = {
  energa: "ENERGA", tauron: "TAURON", inny: "Inny",
};

/** Czy najnowszy skompilowany pakiet jest STARSZY niż najnowsza wersja któregoś z dobranych
    formularzy — czyli powstał przed wgraniem poprawki i ma treść sprzed zmian. Kompilacja
    nie dzieje się sama po wgraniu ODT, więc bez tego znacznika stary plik wygląda jak nowy. */
const isPackageStale = (row: WpwMassRow): boolean => {
  const compiled = row.compilations[0];
  if (!compiled) return false;
  const wanted = new Set(row.effective_form_kinds);
  const newestForm = row.form_documents
    .filter((d) => wanted.has(d.kind))
    .reduce<string>((max, d) => (d.created_at > max ? d.created_at : max), "");
  return newestForm > compiled.created_at;
};

/** Poprzedni (wysłany) list pakietu pod listem kolejnym — z ostrzeżeniem, gdy trasa zmieniła
    się na działce po wysyłce (zamyka go koordynator na karcie działki: „Oznacz jako nieaktualny”). */
function MailPreviousLine({ previous }: { previous: WpwMailPreviousLetter }) {
  return (
    <span className="basis-full pl-4 text-[10px] text-[var(--color-text-muted)]" data-testid="mail-package-previous">
      ↳ poprzedni list: {previous.state_display}
      {previous.print_order_number != null && ` · zlecenie #${previous.print_order_number}`}
      {previous.outdated && (
        <span className="text-amber-700 dark:text-amber-400 font-medium" title={previous.outdated_reason}>
          {" "}⚠ może być nieaktualny — sprawdź na karcie działki (Podstawa dostępu → Listy)
        </span>
      )}
    </span>
  );
}

/** Stan ścieżki listownej w wierszu właściciela (pod pakietem terenowym): brak pakietu /
    gotowy / zaplanowano (termin) / wstrzymano / zlecono wysyłkę (nr) / wysłano / zwrot / wynik.
    Po wysyłce kompilacja składa list KOLEJNY (edytowalny) — wysłany widać linijkę niżej. */
export function MailPackageCell({
  mail,
  onEditPart,
}: {
  mail: WpwMassRow["mail_package"];
  /** Otwórz część listu (pismo przewodnie / kopię formularza) w edytorze online. Brak =
      bez linków „edytuj" (brak uprawnień albo edytor niedostępny). */
  onEditPart?: (part: WpwMailLetterPart) => void;
}) {
  const { openFile } = useManagedFileOpener();
  if (!mail) return null;
  if (!("id" in mail)) {
    // brak pakietu listownego (TAURON / brak szablonu / błąd składania) — z powodem w podpowiedzi
    return (
      <div className="flex items-start gap-1 text-[11px] text-[var(--color-text-muted)] flex-wrap" title={mail.reason} data-testid="mail-package-none">
        <Mail size={11} className="shrink-0 mt-0.5" />
        <span>
          list{mail.previous ? " kolejny" : ""}: brak pakietu
          {mail.reason && <span className="text-amber-700 dark:text-amber-400"> — {mail.reason}</span>}
        </span>
        {mail.previous && <MailPreviousLine previous={mail.previous} />}
      </div>
    );
  }
  const pending = ["ready", "scheduled", "held", "blocked"].includes(mail.state);
  const tone =
    mail.state === "sent" || mail.state === "decided_agreement" ? "text-green-700"
    : mail.state === "held" || mail.state === "blocked" ? "text-amber-700 dark:text-amber-400"
    : mail.state === "returned" ? "text-purple-700 dark:text-purple-400"
    : mail.state === "decided_refusal" || mail.state === "undeliverable" || mail.state === "dispatch_rejected" ? "text-red-600"
    : mail.state === "decided_outdated" ? "text-amber-700 dark:text-amber-400"
    : "text-[var(--color-text-muted)]";
  const detail =
    mail.state === "scheduled" && mail.due_at ? ` · termin ${fmtDate(mail.due_at)}`
    : mail.state === "held" ? ` · ${mail.hold_reason_display}`
    : mail.state === "blocked" ? ` · ${mail.blocked_reason}`
    : mail.print_order ? ` · zlecenie #${mail.print_order.number}${mail.print_order.tracking_number ? `, nr listu ${mail.print_order.tracking_number}` : ""}`
    : mail.state === "cancelled" ? ` · ${mail.cancel_reason_display}`
    : "";
  return (
    <div className="flex items-center gap-1 text-[11px] flex-wrap" data-testid="mail-package-cell">
      <Mail size={11} className="shrink-0" />
      {mail.file?.url ? (
        <a href={mail.file.url} download
           onClick={(e) => { if (!isPlainLeftClick(e)) return; e.preventDefault(); void openFile(mail.file!); }}
           className="underline decoration-dotted font-medium">
          {mail.previous ? "list kolejny" : "list"}
        </a>
      ) : (
        <span>{mail.previous ? "list kolejny" : "list"}</span>
      )}
      <span className={tone} title={mail.last_error?.reason || mail.send_blocker || mail.clock_note || undefined}>{mail.state_display}{detail}</span>
      {mail.is_stale && pending && (
        <span className="text-[10px] text-amber-700 dark:text-amber-400 font-medium" title={mail.stale_reason}>⚠ nieaktualny</span>
      )}
      {mail.outdated && (
        <span className="text-[10px] text-amber-700 dark:text-amber-400 font-medium" data-testid="mail-package-outdated"
              title={`${mail.outdated_reason} — jeśli list jest nieaktualny, oznacz go tak na karcie działki (Podstawa dostępu → Listy), wtedy wyślesz kolejny.`}>
          ⚠ może być nieaktualny — trasa zmieniła się po wysyłce
        </span>
      )}
      {pending && mail.send_blocker && (
        <span className="text-[10px] text-[var(--color-text-muted)]" title={mail.send_blocker}>
          · wyślesz po wyniku poprzedniego
        </span>
      )}
      {mail.previous && <MailPreviousLine previous={mail.previous} />}
      {/* List już zlecony: edycji nie ma, ale „Kompiluj” / „Generuj i kompiluj” złoży list kolejny. */}
      {onEditPart && mail.status === "dispatched" && mail.edit_blocker && (
        <span className="basis-full pl-4 text-[10px] text-[var(--color-text-muted)]" data-testid="mail-package-next-hint">
          {mail.edit_blocker}
        </span>
      )}
      {/* Poprawki listu w edytorze: pismo przewodnie i kopie formularzy. Zmieniają TYLKO list —
          pakiet terenowy zostaje; mapy i wypisu nie da się tu edytować. */}
      {onEditPart && !mail.edit_blocker && (mail.parts ?? []).length > 0 && (
        <span className="basis-full flex items-center gap-x-2 gap-y-0.5 flex-wrap pl-4 text-[10px]" data-testid="mail-package-parts">
          {(mail.parts ?? []).map((part) => (
            <span key={part.part} className="inline-flex items-center gap-1" data-testid="mail-package-part">
              <span className="text-[var(--color-text-secondary)]">
                {part.part === "cover" ? "pismo" : mailPartFormLabel(part)}
              </span>
              {part.has_source ? (
                <button type="button" onClick={() => onEditPart(part)}
                  className="font-medium text-[var(--color-primary)] hover:underline"
                  title={part.part === "cover"
                    ? "Otwórz pismo przewodnie tego listu w edytorze — poprawka trafia tylko do listu"
                    : "Otwórz formularz tego listu w edytorze — poprawka trafia tylko do listu, formularz pakietu terenowego zostaje"}>
                  edytuj
                </button>
              ) : (
                <span className="text-[var(--color-text-muted)]" title="Formularz wgrano jako gotowy PDF — nie ma źródła do edycji">bez źródła</span>
              )}
              {part.error ? (
                <span className="text-red-700 dark:text-red-400 font-medium" title={part.error}>⚠ brak PDF po poprawce</span>
              ) : part.edited ? (
                <span className="text-amber-700 dark:text-amber-400"
                      title={`Poprawione w edytorze${part.edited_by_name ? ` — ${part.edited_by_name}` : ""}${part.edited_at ? `, ${fmtDate(part.edited_at)}` : ""}. Zostaje przy „Kompiluj pakiety”; „Generuj i kompiluj” składa list od nowa z szablonów.`}>
                  ✎ poprawione
                </span>
              ) : null}
            </span>
          ))}
        </span>
      )}
    </div>
  );
}

/** „formularz projektowane" — krótka nazwa rodzaju, a dla nieznanego pełna etykieta sekcji. */
function mailPartFormLabel(part: WpwMailLetterPart): string {
  const short = WPW_ENERGA_KIND_SHORT[part.part as WpwEnergaFormKind];
  return short ? `formularz ${short.toLowerCase()}` : part.label;
}

// Filtr kolumny ścieżki listownej (krok „Generuj i kompiluj" / tryb zlecenia).
const MAIL_FILTERS: { value: string; label: string }[] = [
  { value: "", label: "— ścieżka listowna: wszystkie —" },
  { value: "none", label: "brak pakietu listownego" },
  { value: "pending", label: "czekają na wysyłkę (gotowe/zaplanowane/wstrzymane)" },
  { value: "held", label: "wstrzymane" },
  { value: "blocked", label: "wstrzymane ręcznie" },
  { value: "dispatched", label: "zlecono wysyłkę" },
  { value: "sent", label: "wysłane" },
  { value: "returned", label: "zwrot do rozstrzygnięcia" },
  { value: "decided", label: "z wynikiem" },
  { value: "cancelled", label: "anulowane" },
];

export function matchesMailFilter(row: WpwMassRow, filter: string): boolean {
  if (!filter) return true;
  const state = row.mail_package?.state ?? "none";
  switch (filter) {
    case "pending": return ["ready", "scheduled", "held", "blocked"].includes(state);
    case "decided": return state.startsWith("decided_");
    default: return state === filter;
  }
}

// Kroki kreatora sekwencji (tryb „connection"). Każdy to osobny sub-ekran: pokazuje
// tylko swoje kolumny i akcję. Stan kroku żyje w URL (?krok=…), żeby odświeżenie nie gubiło miejsca.
// Polska liczba mnoga (1 działka / 2 działki / 5 działek) — używana w kreatorze i panelach.
const plural = (n: number, one: string, few: string, many: string) => {
  const m10 = n % 10, m100 = n % 100;
  if (n === 1) return one;
  if (m10 >= 2 && m10 <= 4 && !(m100 >= 12 && m100 <= 14)) return few;
  return many;
};

type StepKey = "dzialki" | "pakiety" | "tresc" | "generuj" | "zlecenie";
// Krok 5: „new" = nowe zamówienie (domyślnie, formularz zapotrzebowania inline);
// „finish" = zakończ sekwencję bez zamówienia (pakiety zapisane, lista zamówień WPW
// projektu do wniosku o zmianę; powrót i zamówienie później z huba).
type OrderMode = "new" | "finish";

// Zamówienia WPW projektu, do których da się dołączyć pakiety wnioskiem o zmianę:
// typ WPW i status żywy — odrzucone/anulowane/zamknięte/zakończone odpadają.
const WPW_ORDER_TYPES: ReadonlySet<string> = new Set(["wpw_kompleksowe", "wpw_terenowe"]);
const CHANGE_CLOSED_STATUSES: ReadonlySet<string> = new Set(["rejected", "cancelled", "closed", "accepted_final"]);
export const wpwChangeTargetOrders = <T extends { order_type: string; status: string }>(orders: T[]): T[] =>
  orders.filter((o) => WPW_ORDER_TYPES.has(o.order_type) && !CHANGE_CLOSED_STATUSES.has(o.status));

const STEPS: { key: StepKey; label: string }[] = [
  { key: "dzialki", label: "Działki" },
  // WNU jest per WŁAŚCICIEL (działka z N współwłaścicielami = N WNU); „pakiet” to
  // dopiero skompilowany PDF (krok 4).
  { key: "pakiety", label: "WNU" },
  { key: "tresc", label: "Formularze i opisy" },
  { key: "generuj", label: "Generuj i kompiluj" },
  { key: "zlecenie", label: "Zlecenie" },
];

// ── zakres zgód działki (droga wewnętrzna z KW, bramka KW, kto jedzie w teren) ──
// Charakter drogi bierzemy WYŁĄCZNIE z księgi — dlatego „brak danych KW" jest tu
// osobnym, widocznym stanem, a nie milczeniem. „nie droga" nie zasługuje na odznakę.
const ROAD_BADGE: Partial<Record<WpwConsentScope["road"]["state"], { label: string; cls: string }>> = {
  internal: {
    label: "droga wewnętrzna (KW)",
    cls: "bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300",
  },
  road: {
    label: "droga (KW) — bez „wewnętrzna”",
    cls: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300",
  },
  unknown: {
    label: "brak danych KW",
    cls: "bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] border border-[var(--color-border-muted)]",
  },
};

// Decyzja koordynatora („droga / nie droga" na działce) przykrywa księgę — wtedy
// odznaka mówi wprost, że to nie werdykt z KW. „Nie droga" ręcznie też ją dostaje,
// żeby nie wyglądało, jakby księga milczała.
const ROAD_BADGE_MANUAL: Partial<Record<WpwConsentScope["road"]["state"], { label: string; cls: string }>> = {
  road: { label: "droga (koordynator)", cls: ROAD_BADGE.road!.cls },
  not_road: {
    label: "nie droga (koordynator)",
    cls: "bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] border border-[var(--color-border-muted)]",
  },
};

function RoadBadge({ road }: { road: WpwConsentScope["road"] }) {
  const badge = (road.manual && ROAD_BADGE_MANUAL[road.state]) || ROAD_BADGE[road.state];
  if (!badge) return null;
  return (
    <span
      title={road.note}
      className={`inline-flex items-center rounded px-1 py-px text-[9px] font-mono uppercase tracking-wide ${badge.cls}`}
    >
      {badge.label}
    </span>
  );
}

// Wiersz jednej JEDNOSTKI ZGODY: kto podpisuje, jaki ma udział i czy jedzie w teren.
// Małżeństwo we wspólności to jeden wiersz o dwóch WNU — checkbox rusza oba naraz.
function ConsentUnitRow({
  unit, disabled, onToggle, onReason,
}: {
  unit: WpwConsentUnit;
  disabled: boolean;
  onToggle: (excluded: boolean) => void;
  onReason: (reason: string) => void;
}) {
  const [reason, setReason] = useState(unit.excluded_reason);
  useEffect(() => { setReason(unit.excluded_reason); }, [unit.excluded_reason]);
  const noPackage = unit.applications.length === 0;
  return (
    <div className={`px-4 py-1.5 text-[11px] ${unit.excluded ? "opacity-60" : ""}`}>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={!unit.excluded}
          disabled={disabled || noPackage}
          onChange={(e) => onToggle(!e.target.checked)}
          title={noPackage
            ? "Najpierw załóż WNU dla właścicieli tej działki"
            : disabled
              ? "Jedyna jednostka zgody na działce — musi iść w teren"
              : "Odznacz, jeśli ten właściciel NIE ma iść w teren"}
        />
        <span className="flex-1 truncate text-[var(--color-text-secondary)]">
          {unit.label || "—"}
          {unit.marital && (
            <span className="ml-1 text-[9px] font-mono uppercase text-[var(--color-text-muted)]"
              title={unit.owner_ids.length > 2
                ? `Wspólna jednostka zgody według wypisu (${unit.owner_ids.length} osoby) — jedna zgoda, ${unit.owner_ids.length} WNU`
                : "Wspólność małżeńska według wypisu — jedna zgoda, dwa WNU"}>
              {unit.owner_ids.length > 2 ? `wspólność (${unit.owner_ids.length} os.)` : "małżeństwo"}
            </span>
          )}
        </span>
        <span className="shrink-0 font-mono text-[var(--color-text-primary)]"
          title={unit.share_raw ? `Udział z dokumentu: ${unit.share_raw}` : "Wypis nie podaje udziału"}>
          {unit.share_text || (unit.share_raw ? unit.share_raw : "udział ?")}
        </span>
        <span className="shrink-0 w-28 text-right">
          {unit.applications.length > 0 ? (
            <Link to={`/power-projects/wnioski/${unit.applications[0].id}`}
              className="font-mono text-[var(--color-primary)] hover:underline" title="Otwórz WNU">
              {unit.applications[0].number}
              {unit.applications.length > 1 && ` +${unit.applications.length - 1}`}
            </Link>
          ) : (
            <span className="text-[10px] font-mono uppercase tracking-wide text-amber-700 dark:text-amber-400">
              do założenia
            </span>
          )}
        </span>
      </div>
      {unit.excluded && (
        <div className="mt-1 pl-6 flex items-center gap-2">
          <span className="text-[10px] font-mono uppercase tracking-wide text-[var(--color-text-muted)]">
            nie idzie w teren
          </span>
          <input
            value={reason}
            disabled={disabled}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => { if (reason !== unit.excluded_reason) onReason(reason); }}
            placeholder="powód (np. zgody od większości udziałów)"
            className="flex-1 rounded border border-[var(--color-border-muted)] bg-[var(--color-bg-surface)] px-1.5 py-0.5 text-[10px]"
          />
        </div>
      )}
    </div>
  );
}

// Wątpliwości analizy mapy (etykiety CAD czytane obok nazw warstw): każda z nich
// blokuje pakiet, dopóki człowiek nie zatwierdzi zakresu. Zatwierdzenie zapisuje
// BIEŻĄCE zaznaczenie formularzy jako dobór ręczny i jest ważne tylko dla tego
// zestawu wątpliwości — zmiana mapy otwiera pytanie na nowo.
export function ScopeReviewNotice({
  review, effectiveKinds, disabled, pending, error = null, onConfirm, onClear,
}: {
  review: WpwMassScopeReview | undefined;
  effectiveKinds: string[];
  disabled: boolean;
  pending: boolean;
  /** Błąd ostatniego zatwierdzania TEJ działki — dotąd ginął po cichu, a wiersz wyglądał jak „nie działa". */
  error?: string | null;
  onConfirm: (kinds: string[]) => void;
  onClear: () => void;
}) {
  if (!review || review.doubts.length === 0) return null;
  if (review.confirmed) {
    return (
      <p className="mt-1 text-[10px] leading-snug text-green-700 dark:text-green-400 inline-flex items-start gap-1">
        <ShieldCheck size={11} className="mt-px shrink-0" />
        <span>
          zakres zatwierdzony{review.confirmed.by ? ` — ${review.confirmed.by}` : ""}
          {review.confirmed.at ? `, ${fmtDate(review.confirmed.at)}` : ""}
          {review.confirmed.note ? ` („${review.confirmed.note}”)` : ""}
          <button type="button" disabled={disabled || pending} onClick={onClear}
            className="ml-1.5 font-mono uppercase text-[9px] text-[var(--color-text-secondary)] hover:underline disabled:opacity-50">
            cofnij
          </button>
        </span>
      </p>
    );
  }
  return (
    <div className="mt-1 text-[10px] leading-snug text-amber-800 dark:text-amber-300 border-l-2 border-amber-400 pl-1.5 space-y-0.5"
      data-testid="scope-review-notice">
      <p className="font-medium inline-flex items-center gap-1">
        <AlertTriangle size={11} className="shrink-0" />
        Etykiety DXF budzą wątpliwości co do zakresu — „Przygotuj i sprawdź” zatrzyma się do zatwierdzenia
        {review.stale && <span className="font-mono uppercase text-[9px]">(mapa zmieniona po zatwierdzeniu)</span>}
      </p>
      <ul className="list-disc pl-3 space-y-0.5">
        {review.doubts.map((d, i) => (
          <li key={`${d.code}-${i}`}>{d.reason}</li>
        ))}
      </ul>
      <p className="text-[var(--color-text-secondary)]">
        Standardowa ścieżka (Generuj i kompiluj / Kompiluj pakiety) etykiet nie czyta i nie jest tym
        blokowana. Sprawdź mapę, zaznacz właściwe formularze powyżej i zatwierdź.
      </p>
      <Button size="sm" variant="secondary" disabled={disabled || effectiveKinds.length === 0} loading={pending}
        onClick={() => onConfirm(effectiveKinds)}
        title={effectiveKinds.length === 0
          ? "Najpierw zaznacz co najmniej jeden formularz"
          : "Zatwierdź zaznaczone formularze jako zakres tej działki (dobór ręczny)"}>
        Zatwierdź zakres
      </Button>
      {error && <p className="text-red-600 dark:text-red-400" data-testid="scope-review-error">{error}</p>}
    </div>
  );
}

// Wiersze overview są per (działka × właściciel). Tabela kroków 3/4 pokazuje JEDEN wiersz
// na działkę, a właścicieli z ich WNU wypisuje pod nią (zgłoszenie 17.09.2026) — dotąd
// każdy współwłaściciel był osobnym wierszem z tym samym doborem, opisem i wątpliwościami.
export function groupRowsByParcel(rows: WpwMassRow[]): Map<string, WpwMassRow[]> {
  const byParcel = new Map<string, WpwMassRow[]>();
  for (const r of rows) {
    const list = byParcel.get(r.parcel.id) ?? [];
    list.push(r);
    byParcel.set(r.parcel.id, list);
  }
  return byParcel;
}

/** Klucz rozwinięcia „pola szablonów" — per (działka, właściciel), bo pola są per WNU. */
export const fieldsKeyOf = (r: WpwMassRow) => `${r.parcel.id}:${r.owner?.id ?? ""}`;

/** Blokady działki zebrane z wierszy jej właścicieli, bez powtórzeń; `partial` = nie każdy
 *  WNU działki ją ma (np. brak formularza jednego właściciela) — wtedy wypisujemy czyja. */
export function blockedAcrossOwners(group: WpwMassRow[]): (WpwMassBlocked & { owners: string[]; partial: boolean })[] {
  const out = new Map<string, WpwMassBlocked & { owners: string[]; partial: boolean }>();
  for (const r of group) {
    for (const b of r.blocked) {
      const key = `${b.code}|${b.reason}`;
      const acc = out.get(key) ?? { ...b, owners: [], partial: false };
      if (r.owner && !acc.owners.includes(r.owner.full_name)) acc.owners.push(r.owner.full_name);
      out.set(key, acc);
    }
  }
  const ownerCount = group.filter((r) => r.owner).length;
  return [...out.values()].map((b) => ({ ...b, partial: ownerCount > 1 && b.owners.length < ownerCount }));
}

/** Kroki 3/4 i tryb zlecenia: każdy współwłaściciel działki ze swoim WNU, w jednym wierszu. */
export function ParcelOwnersList({ group }: { group: WpwMassRow[] }) {
  const listed = group.filter((r) => r.owner);
  if (listed.length === 0) {
    return <span className="text-amber-700 dark:text-amber-400">brak właściciela</span>;
  }
  return (
    <ul className="space-y-0.5" data-testid="parcel-owners">
      {listed.map((r) => (
        <li key={r.owner!.id} className="flex flex-wrap items-baseline gap-x-1.5">
          <span className="font-medium text-[var(--color-text-secondary)]">{r.owner!.full_name}</span>
          {r.application ? (
            <Link
              to={`/power-projects/wnioski/${r.application.id}`}
              className="font-mono text-[var(--color-primary)] hover:underline"
              title="Otwórz WNU"
            >
              {r.application.number}
            </Link>
          ) : (
            <span className="text-amber-700 dark:text-amber-400">bez WNU</span>
          )}
          {r.application?.status === "return_received" && (
            <span className="text-[10px] font-mono uppercase text-green-700">zwrot ✓</span>
          )}
          {r.field_excluded && (
            <span className="text-[10px] font-mono uppercase text-[var(--color-text-muted)]"
              title={r.field_excluded_reason || "Pominięty świadomie w kroku „WNU”"}>
              nie idzie w teren
            </span>
          )}
        </li>
      ))}
      {listed.length > 1 && (
        <li className="text-[9px] text-amber-700">
          {listed.length} WNU
          {/* zgód bywa mniej niż WNU — małżeństwo podpisuje raz */}
          {group[0].parcel.consent.unit_count > 1 && group[0].parcel.consent.unit_count !== listed.length && (
            <span className="text-[var(--color-text-muted)]"> · {group[0].parcel.consent.unit_count} zgód</span>
          )}
        </li>
      )}
    </ul>
  );
}

// Bramka „bez analizy KW ani kroku dalej" — powód wprost z backendu + trzy wyjścia:
// pobrać/przeanalizować księgę, świadomie zwolnić działkę z wymogu (z powodem) albo —
// przy blokadzie POMIJALNEJ (błąd analizy: brak właścicieli z Działu II) — pominąć
// błąd po potwierdzeniu (powód nadaje backend, zwolnienie da się cofnąć).
export function KwGateNotice({
  gate, disabled, onOpenKw, onOverride, onClearOverride, onSkip,
}: {
  gate: WpwConsentScope["kw_gate"];
  disabled: boolean;
  onOpenKw: () => void;
  onOverride: (reason: string) => void;
  onClearOverride: () => void;
  onSkip?: () => void;
}) {
  const [draft, setDraft] = useState("");
  const [asking, setAsking] = useState(false);
  const [skipAsking, setSkipAsking] = useState(false);
  // Rozbieżność właścicieli z KW — OSTRZEŻENIE niezależne od progu bramki: druk idzie,
  // ale człowiek ma to zobaczyć (i potwierdzić przed generacją — KwWarningConfirmModal).
  const warning = gate.warning && !gate.blocker ? (
    <div className="px-4 py-2 bg-amber-50 dark:bg-amber-500/10 border-b border-amber-200 dark:border-amber-500/20"
         data-testid="kw-gate-warning">
      <div className="flex items-start gap-2">
        <AlertTriangle size={14} className="mt-px shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="flex-1 text-[11px] text-amber-900 dark:text-amber-200">
          <span className="font-mono uppercase text-[10px] tracking-wide">ostrzeżenie (nie blokuje): </span>
          {gate.warning.reason}
          <div className="mt-1.5">
            <Button size="sm" variant="secondary" onClick={onOpenKw}>Otwórz KW działki</Button>
          </div>
        </div>
      </div>
    </div>
  ) : null;
  if (!gate.required) return warning;

  if (gate.override) {
    return (
      <div className="px-4 py-2 text-[11px] bg-[var(--color-bg-elevated)] border-b border-[var(--color-border-muted)]">
        <span className="text-[var(--color-text-secondary)]">
          Zwolniona z wymogu analizy KW — {gate.override.reason}
          {gate.override.by && <span className="text-[var(--color-text-muted)]"> ({gate.override.by})</span>}
        </span>
        <button type="button" disabled={disabled} onClick={onClearOverride}
          className="ml-2 font-mono uppercase text-[10px] text-[var(--color-primary)] hover:underline disabled:opacity-50">
          przywróć wymóg
        </button>
      </div>
    );
  }
  if (!gate.blocker) {
    if (warning) return warning;
    return (
      <div className="px-4 py-1.5 text-[11px] inline-flex items-center gap-1.5 text-green-700 dark:text-green-400">
        <ShieldCheck size={13} /> analiza KW zgodna z właścicielami działki
      </div>
    );
  }
  return (
    <div className="px-4 py-2 bg-amber-50 dark:bg-amber-500/10 border-b border-amber-200 dark:border-amber-500/20">
      <div className="flex items-start gap-2">
        <AlertTriangle size={14} className="mt-px shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="flex-1 text-[11px] text-amber-900 dark:text-amber-200">
          {gate.blocker.reason}
          {gate.blocker.skippable && onSkip && (
            <div className="mt-1.5 flex flex-wrap items-center gap-2" data-testid="kw-gate-skip">
              {!skipAsking ? (
                <Button size="sm" variant="secondary" disabled={disabled} onClick={() => setSkipAsking(true)}
                  title="Pakiety powstaną dla właścicieli wpisanych na działce, bez weryfikacji z Działem II księgi — zwolnienie zapisze się na działce z Twoim nazwiskiem i da się cofnąć">
                  Pomiń błąd analizy KW — generuj mimo to
                </Button>
              ) : (
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px]">
                    Na pewno? Zgody pójdą do właścicieli z działki bez sprawdzenia z księgą.
                  </span>
                  <Button size="sm" disabled={disabled} data-testid="kw-gate-skip-confirm"
                    onClick={() => { onSkip(); setSkipAsking(false); }}>
                    Tak, pomiń błąd
                  </Button>
                  <button type="button" onClick={() => setSkipAsking(false)}
                    className="font-mono uppercase text-[10px] text-[var(--color-text-muted)] hover:underline">
                    anuluj
                  </button>
                </span>
              )}
            </div>
          )}
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <Button size="sm" variant="secondary" onClick={onOpenKw}>Otwórz KW działki</Button>
            {!asking ? (
              <button type="button" disabled={disabled} onClick={() => setAsking(true)}
                className="font-mono uppercase text-[10px] text-[var(--color-text-secondary)] hover:underline disabled:opacity-50"
                title="Gdy księgi po prostu nie ma (działka bez urządzonej KW) — zdejmij wymóg z powodem">
                księgi nie ma — zwolnij
              </button>
            ) : (
              <span className="flex items-center gap-1.5">
                <input
                  autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
                  placeholder="powód zwolnienia (wymagany)"
                  className="rounded border border-[var(--color-border-muted)] bg-[var(--color-bg-surface)] px-1.5 py-0.5 text-[10px] w-64"
                />
                <Button size="sm" variant="secondary" disabled={disabled || !draft.trim()}
                  onClick={() => { onOverride(draft.trim()); setAsking(false); setDraft(""); }}>
                  Zwolnij
                </Button>
                <button type="button" onClick={() => setAsking(false)}
                  className="font-mono uppercase text-[10px] text-[var(--color-text-muted)] hover:underline">
                  anuluj
                </button>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Karta jednej działki w kroku „WNU": kto podpisuje, z jakim udziałem, kto jedzie
// w teren — plus podpowiedź większości (droga wewnętrzna) albo przypomnienie o
// dopytaniu ZE (≥6 zgód, a księga drogi wewnętrznej nie wskazuje).
function ConsentScopeCard({
  parcel, disabled, onOpenKw, onExclude, onOverride, onClearOverride, onSkip,
}: {
  parcel: WpwMassRow["parcel"];
  disabled: boolean;
  onOpenKw: () => void;
  onExclude: (ownerIds: string[], excluded: boolean, reason?: string) => void;
  onOverride: (reason: string) => void;
  onClearOverride: () => void;
  onSkip: () => void;
}) {
  const scope = parcel.consent;
  const multi = scope.unit_count > 1;
  return (
    <div className="border-b border-[var(--color-border-muted)] last:border-b-0">
      <div className="flex flex-wrap items-center gap-2 px-4 py-2 bg-[var(--color-bg-elevated)]">
        <span className="font-mono text-[11px] font-semibold text-[var(--color-text-primary)]">
          {parcel.teryt}
        </span>
        {parcel.short_number && (
          <span className="text-[11px] text-[var(--color-text-secondary)]">dz. {parcel.short_number}</span>
        )}
        <RoadBadge road={scope.road} />
        <span className="text-[10px] font-mono uppercase tracking-wide text-[var(--color-text-muted)]">
          {scope.unit_count} {scope.unit_count === 1 ? "zgoda" : "zgód"}
          {scope.total_share.text && ` · razem ${scope.total_share.text}`}
        </span>
      </div>

      {scope.hint && (
        <div className="px-4 py-2 text-[11px] bg-rose-50 dark:bg-rose-500/10 border-b border-rose-200 dark:border-rose-500/20 text-rose-900 dark:text-rose-200">
          {scope.hint.text}
        </div>
      )}
      {scope.alert && (
        <div className="px-4 py-2 text-[11px] bg-amber-50 dark:bg-amber-500/10 border-b border-amber-200 dark:border-amber-500/20 text-amber-900 dark:text-amber-200">
          {scope.alert.text}
        </div>
      )}

      <KwGateNotice gate={scope.kw_gate} disabled={disabled} onOpenKw={onOpenKw}
        onOverride={onOverride} onClearOverride={onClearOverride} onSkip={onSkip} />

      <div className="divide-y divide-[var(--color-border-muted)]">
        {scope.units.map((unit) => (
          <ConsentUnitRow
            key={unit.key}
            unit={unit}
            // Przy jednym właścicielu nie ma czego wybierać — odznaczenie zostawiłoby
            // działkę bez ani jednej zgody.
            disabled={disabled || !multi}
            onToggle={(excluded) => onExclude(unit.owner_ids, excluded)}
            onReason={(reason) => onExclude(unit.owner_ids, true, reason)}
          />
        ))}
        {scope.units.length === 0 && (
          <div className="px-4 py-3 text-[11px] text-[var(--color-text-muted)]">
            Działka nie ma właścicieli prywatnych — nie ma komu wystawić WNU.
          </div>
        )}
      </div>

      {multi && (
        <div className="px-4 py-1.5 text-[10px] font-mono uppercase tracking-wide bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)]">
          w teren: {scope.selected_units}/{scope.unit_count}
          {scope.selected_share.text && ` · ${scope.selected_share.text} udziałów`}
          {scope.selected_share.unknown_units > 0 &&
            ` (+${scope.selected_share.unknown_units} bez czytelnego udziału)`}
          {scope.hint && (
            <span className={scope.hint.reached
              ? " text-green-700 dark:text-green-400"
              : " text-rose-700 dark:text-rose-400"}>
              {" "}· cel: ponad 50% — {scope.hint.reached ? "osiągnięty" : "jeszcze nie"}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function StepperHeader({ step, onGo }: { step: StepKey; onGo: (k: StepKey) => void }) {
  const idx = STEPS.findIndex((s) => s.key === step);
  return (
    <nav className="flex items-center">
      {STEPS.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        return (
          <Fragment key={s.key}>
            {i > 0 && (
              <div className={`h-px flex-1 mx-1 ${i <= idx ? "bg-[var(--color-primary)]" : "bg-[var(--color-border)]"}`} />
            )}
            <button
              type="button"
              disabled={i > idx}
              onClick={() => onGo(s.key)}
              className={`group flex items-center gap-2 ${i > idx ? "cursor-default" : "cursor-pointer"}`}
              title={s.label}
            >
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold transition-colors ${
                  active ? "bg-[var(--color-primary)] text-white ring-2 ring-[var(--color-primary)]/30"
                    : done ? "bg-green-600 text-white"
                    : "bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] border border-[var(--color-border)]"
                }`}
              >
                {done ? "✓" : i + 1}
              </span>
              <span
                className={`hidden sm:block text-xs font-medium whitespace-nowrap ${
                  active ? "text-[var(--color-text-primary)]"
                    : done ? "text-[var(--color-text-secondary)]"
                    : "text-[var(--color-text-muted)]"
                }`}
              >
                {s.label}
              </span>
            </button>
          </Fragment>
        );
      })}
    </nav>
  );
}

function ReadyChip({ ok, label, reason }: { ok: boolean; label: string; reason?: string }) {
  return (
    <span
      title={reason || label}
      className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5 border ${
        ok
          ? "text-green-700 border-green-300 bg-green-50/60 dark:text-green-400 dark:border-green-500/40 dark:bg-green-500/10"
          : "text-red-700 border-red-300 bg-red-50/60 dark:text-red-400 dark:border-red-500/40 dark:bg-red-500/10"
      }`}
    >
      {ok ? <CheckCircle2 size={11} /> : <XCircle size={11} />} {label}
    </span>
  );
}

// Obiektywny powód, dla którego pole nie jest edytowalne inline (zamiast ogólnego
// „uzupełnij w źródle"). Pola prostych obiektów (działka/klient/projekt/właściciel-1)
// SĄ edytowalne; poniżej tylko te, których z natury nie da się wpisać tutaj.
function lockReason(f: DocTemplateField): string {
  if (f.ai) return "liczone przez AI — użyj „Generuj AI” w opisie urządzeń";
  const src = (f.source || "").toLowerCase();
  if (src.includes("mapa")) return "liczone z warstw mapy (geometria) — zmienia się z trasą, nie wpisuje się ręcznie";
  if (f.name.startsWith("wlasciciel_") || src.includes("właścic"))
    return "↓ ustaw w sekcji „Dane powiązane → Właściciele działki” poniżej";
  if (f.name === "projekt_projektant" || src.includes("projektant"))
    return "↓ ustaw w sekcji „Dane powiązane → Projektant” poniżej (konto osoby, nie tekst)";
  return `uzupełnij w źródle: ${f.source || "—"}`;
}

// Select projektanta (konto osoby w systemie) — ustawia connection.designer, więc
// #projekt_projektant# uzupełni się we WSZYSTKICH działkach projektu (wspólne).
function DesignerPicker({ connectionId, onSaved }: { connectionId: string; onSaved: () => void }) {
  const { data: users } = useInternalUsers();
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const save = async () => {
    setBusy(true); setMsg("");
    try {
      await updateConnection(connectionId, { designer: val || null } as never);
      setMsg("Zapisano — projektant uzupełni się we wszystkich działkach projektu.");
      onSaved();
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-[var(--color-border-muted)] p-2 space-y-1.5">
      <div className="flex items-center gap-1 text-[11px] font-medium">
        <span className="font-mono text-amber-700 dark:text-amber-400">#projekt_projektant#</span>
        <span className="rounded px-1 bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300 text-[9px] font-mono uppercase">wspólne</span>
      </div>
      <p className="text-[10px] text-[var(--color-text-muted)]">Projektant to konto osoby — wybierz z listy (nie wpisujesz nazwiska).</p>
      <div className="flex items-center gap-2">
        <select className="input-base text-[11px] py-0.5 flex-1" value={val} onChange={(e) => setVal(e.target.value)}>
          <option value="">— wybierz projektanta —</option>
          {(users ?? []).map((u) => (
            <option key={u.id} value={u.id}>{u.full_name || u.username}</option>
          ))}
        </select>
        <Button size="sm" disabled={!val || busy} onClick={save}>
          {busy ? <Spinner className="h-3 w-3 mr-1" /> : null} Ustaw
        </Button>
      </div>
      {msg && <p className="text-[11px] text-green-700 flex items-center gap-1"><CheckCircle2 size={12} />{msg}</p>}
    </div>
  );
}

// Formularz właściciela(-i) działki inplace — wypełnia #wlasciciel_imie_nazwisko#,
// #wlasciciel_adres# (rozbity na ulica/nr/kod/miasto), #wlasciciel_pesel#. Rekord jest
// per działka (nie wspólny). Reużywa istniejący rejestr właścicieli (parcel-owners).
function OwnerEditor({ parcelId, onSaved }: { parcelId: string; onSaved: () => void }) {
  const qc = useQueryClient();
  const { data: owners, isLoading } = useQuery({
    queryKey: ["pp", "parcel-owners", parcelId] as const,
    queryFn: () => getParcelOwners(parcelId),
  });
  const [edits, setEdits] = useState<Record<string, Partial<ParcelOwner>>>({});
  const [busy, setBusy] = useState(false);
  const OWNER_FIELDS: { key: keyof ParcelOwner; label: string; w?: string }[] = [
    { key: "full_name", label: "Nazwisko i imię / nazwa", w: "col-span-2" },
    { key: "street", label: "Ulica" },
    { key: "building_no", label: "Nr" },
    { key: "postal_code", label: "Kod" },
    { key: "city", label: "Miejscowość" },
    { key: "pesel_nip", label: "PESEL / NIP" },
    { key: "ownership_share", label: "Udział (np. 1/1)" },
    { key: "role", label: "Rola (opcjonalnie)" },
  ];
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["pp", "parcel-owners", parcelId] });
    onSaved();
  };
  const val = (o: ParcelOwner, k: keyof ParcelOwner) =>
    (edits[o.id]?.[k] as string | undefined) ?? ((o[k] as string) ?? "");
  const saveOwner = async (o: ParcelOwner) => {
    const patch = edits[o.id];
    if (!patch || !Object.keys(patch).length) return;
    setBusy(true);
    try { await updateOwner(o.id, patch); setEdits((p) => { const n = { ...p }; delete n[o.id]; return n; }); refresh(); }
    finally { setBusy(false); }
  };
  const addOwner = async () => {
    setBusy(true);
    try { await createOwner({ parcel: parcelId, kind: "person", full_name: "" }); refresh(); }
    finally { setBusy(false); }
  };
  if (isLoading) return <div className="py-2"><Spinner className="h-4 w-4" /></div>;
  return (
    <div className="rounded-md border border-[var(--color-border-muted)] p-2 space-y-2">
      <div className="text-[11px] font-medium">
        Właściciele działki <span className="text-[10px] text-[var(--color-text-muted)]">— wypełnia #wlasciciel_imie_nazwisko#, #wlasciciel_adres#, #wlasciciel_pesel#</span>
      </div>
      {(owners ?? []).length === 0 && (
        <p className="text-[11px] text-[var(--color-text-muted)]">Działka nie ma właściciela — dodaj, aby uzupełnić dane adresata WPW.</p>
      )}
      {(owners ?? []).map((o) => {
        const dirty = !!edits[o.id] && Object.keys(edits[o.id]).length > 0;
        return (
          <div key={o.id} className="border border-[var(--color-border-muted)] p-1.5 space-y-1">
            <div className="grid grid-cols-2 gap-1">
              {OWNER_FIELDS.map((f) => (
                <label key={f.key} className={`text-[10px] text-[var(--color-text-muted)] ${f.w ?? ""}`}>
                  {f.label}
                  {f.key === "role" ? (
                    <select className="input-base w-full text-[11px] py-0.5 mt-0.5"
                      value={val(o, f.key)}
                      onChange={(e) => setEdits((p) => ({ ...p, [o.id]: { ...p[o.id], role: e.target.value as ParcelOwner["role"] } }))}>
                      <option value="">— nie wskazano —</option>
                      {OWNER_ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  ) : (
                    <input className="input-base w-full text-[11px] py-0.5 mt-0.5"
                      value={val(o, f.key)}
                      onChange={(e) => setEdits((p) => ({ ...p, [o.id]: { ...p[o.id], [f.key]: e.target.value } }))} />
                  )}
                </label>
              ))}
            </div>
            {dirty && (
              <Button size="sm" disabled={busy} onClick={() => saveOwner(o)}>
                {busy ? <Spinner className="h-3 w-3 mr-1" /> : null} Zapisz właściciela
              </Button>
            )}
          </div>
        );
      })}
      <Button size="sm" variant="secondary" disabled={busy} onClick={addOwner}>+ Dodaj właściciela</Button>
    </div>
  );
}

// Rozkład pól #zmiennych# szablonów działki: co wypełni się automatycznie, co
// wyjdzie puste (i GDZIE to uzupełnić — działka / klient / projekt / metadane),
// a co jest w tym kontekście nieosiągalne. W kroku 3 (przed generacją) pola puste,
// które da się zapisać, są EDYTOWALNE inline z „zapisz do bazy" (writeback);
// projektant → select osoby, właściciel → formularz właściciela (sekcje poniżej).
function ParcelFieldsPanel({ parcelId, owner, connectionId, canMutate }: { parcelId: string; owner?: string; connectionId: string; canMutate: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useWpwMassParcelFields(parcelId, owner);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  // pola z wartością, których backend NIE zapisał (liczone przez AI) — ostrzegamy
  // zamiast pozorować sukces; opis AI edytuje się osobnym polem „Opis urządzeń".
  const [droppedMsg, setDroppedMsg] = useState("");

  const dirty = Object.keys(edits).length > 0;
  const save = async () => {
    const values = Object.fromEntries(
      Object.entries(edits).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v),
    );
    if (!Object.keys(values).length) return;
    setSaving(true);
    setSavedMsg("");
    setDroppedMsg("");
    try {
      const res = await persistWpwMassParcelFields(parcelId, values, owner);
      setEdits({});
      setSavedMsg(`Zapisano ${res.applied?.length ?? 0} pól do bazy.`);
      if (res.dropped?.length) {
        setDroppedMsg(
          `Nie zapisano (liczone przez AI): ${res.dropped.map((n) => `#${n}#`).join(", ")}`
          + " — opis urządzeń uzupełnij osobnym polem „Opis urządzeń” (Generuj AI lub ręcznie).",
        );
      }
      qc.setQueryData(["pp", "wpw-mass-parcel-fields", parcelId, owner ?? ""], res);
      qc.invalidateQueries({ queryKey: ["pp", "wpw-mass-overview"] });
      // liczniki „do uzupełnienia" na wierszach WSPÓŁWŁAŚCICIELI tej działki (pole
      // działki jest wspólne) — bez tego świeżyły się dopiero po F5
      qc.invalidateQueries({ queryKey: ["pp", "wpw-mass-parcel-fields"] });
    } finally {
      setSaving(false);
    }
  };

  // Po zapisie powiązanego rekordu (projektant/właściciel) — przelicz pola i overview.
  const refreshFields = () => {
    qc.invalidateQueries({ queryKey: ["pp", "wpw-mass-parcel-fields"] });
    qc.invalidateQueries({ queryKey: ["pp", "wpw-mass-overview"] });
  };

  if (isLoading) return <div className="py-2 flex justify-center"><Spinner className="h-4 w-4" /></div>;
  if (isError || !data) {
    return <p className="text-[11px] text-red-600 py-1">Nie udało się pobrać pól szablonów (działka musi mieć wniosek).</p>;
  }
  // Pola wymagające dedykowanego formularza (nie wolny tekst): projektant → select osoby,
  // właściciel → formularz właściciela. Wykrywamy je wśród pól pustych, nieedytowalnych.
  const allEmpty = data.kinds.flatMap((k) => k.fields.filter((f) => f.status === "empty"));
  const needDesigner = canMutate && allEmpty.some((f) => f.name === "projekt_projektant");
  // Właściciela zawsze edytujemy dedykowanym formularzem (adres jest rozbity) — nie inline.
  const needOwner = canMutate && allEmpty.some((f) => f.name.startsWith("wlasciciel_"));
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-3 space-y-3">
      <div className="flex items-center gap-2 text-[11px] text-[var(--color-text-muted)]">
        <span className="font-semibold text-[var(--color-text-primary)]">Pola szablonów</span>
        <span>— uzupełnij PRZED generacją. Pola oznaczone</span>
        <span className="inline-flex items-center rounded px-1 bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300 text-[9px] font-mono uppercase">wspólne</span>
        <span>zapiszą się też dla pozostałych działek projektu.</span>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {data.kinds.map((k) => {
          const empty = k.fields.filter((f) => f.status === "empty");
          const missing = k.fields.filter((f) => f.status === "missing");
          const filled = k.fields.length - empty.length - missing.length;
          const complete = empty.length === 0 && missing.length === 0;
          return (
            <div key={k.kind} className="rounded-md border border-[var(--color-border-muted)] overflow-hidden">
              <div className="flex items-center justify-between gap-2 px-2 py-1.5 bg-[var(--color-bg-elevated)] border-b border-[var(--color-border-muted)]">
                <span className="text-[10px] font-mono uppercase tracking-wide font-semibold">
                  {WPW_ENERGA_KIND_SHORT[k.kind as WpwEnergaFormKind] ?? k.kind}
                </span>
                <span className="text-[9px] font-mono text-[var(--color-text-muted)]" title={k.template_name ?? ""}>
                  ✓{filled} · ✎{empty.length} · ✕{missing.length}
                </span>
              </div>
              <div className="p-2 space-y-2">
                {complete && (
                  <p className="text-[11px] text-green-700 dark:text-green-400 flex items-center gap-1">
                    <CheckCircle2 size={12} /> Komplet — wszystkie pola mają dane.
                  </p>
                )}
                {empty.map((f) => {
                  // pola właściciela → dedykowany formularz poniżej (nie inline tekst)
                  const editable = f.writable && !f.ai && canMutate && !f.name.startsWith("wlasciciel_");
                  return (
                    <div key={f.name} className="space-y-0.5">
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="font-mono text-[11px] text-amber-700 dark:text-amber-400">#{f.name}#</span>
                        {f.write_shared && (
                          <span className="rounded px-1 bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300 text-[9px] font-mono uppercase">wspólne</span>
                        )}
                        {f.ai && <span className="rounded px-1 bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300 text-[9px] font-mono uppercase">AI</span>}
                      </div>
                      {editable ? (
                        <input
                          className="input-base w-full text-[11px] py-0.5"
                          placeholder={`${f.write_target || f.source}…`}
                          value={edits[f.name] ?? ""}
                          onChange={(e) => setEdits((p) => ({ ...p, [f.name]: e.target.value }))}
                        />
                      ) : (
                        <p className="text-[10px] text-[var(--color-text-muted)]">{lockReason(f)}</p>
                      )}
                    </div>
                  );
                })}
                {missing.map((f) => (
                  <div key={f.name} className="space-y-0.5">
                    <span className="font-mono text-[11px] text-red-600">#{f.name}#</span>
                    <p className="text-[10px] text-[var(--color-text-muted)]">
                      niedostępna w tym kontekście{f.source ? ` — źródło: ${f.source}` : ""}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {data.kinds.length === 0 && (
          <p className="text-[11px] text-[var(--color-text-muted)]">Brak dobranych formularzy dla działki.</p>
        )}
      </div>
      {(needDesigner || needOwner) && (
        <div className="space-y-2 pt-1 border-t border-[var(--color-border-muted)]">
          <p className="text-[11px] font-semibold text-[var(--color-text-primary)]">
            Dane powiązane <span className="font-normal text-[10px] text-[var(--color-text-muted)]">— nie są wolnym tekstem, ustaw je odpowiednim formularzem:</span>
          </p>
          {needDesigner && <DesignerPicker connectionId={connectionId} onSaved={refreshFields} />}
          {needOwner && <OwnerEditor parcelId={parcelId} onSaved={refreshFields} />}
        </div>
      )}
      {(dirty || savedMsg || droppedMsg) && (
        <div className="space-y-1 pt-1 border-t border-[var(--color-border-muted)]">
          <div className="flex items-center gap-2">
            {dirty && (
              <Button size="sm" disabled={saving} onClick={save}>
                {saving ? <Spinner className="h-3 w-3 mr-1" /> : null} Zapisz do bazy
              </Button>
            )}
            {dirty && Object.keys(edits).some((k) => allEmpty.find((f) => f.name === k)?.write_shared) && (
              <span className="text-[10px] text-[var(--color-text-muted)]">wspólne pola uzupełnią też inne działki</span>
            )}
            {savedMsg && <span className="text-[11px] text-green-700 flex items-center gap-1"><CheckCircle2 size={12} />{savedMsg}</span>}
          </div>
          {droppedMsg && (
            <p className="text-[11px] text-amber-700 dark:text-amber-400 flex items-start gap-1">
              <AlertTriangle size={12} className="shrink-0 mt-0.5" />{droppedMsg}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// Liczba pól do uzupełnienia (puste+nieosiągalne) na przycisku „pola szablonów".
function ParcelFieldsCount({ parcelId, owner, enabled }: { parcelId: string; owner?: string; enabled: boolean }) {
  const { data } = useWpwMassParcelFields(parcelId, owner, enabled);
  if (!data) return null;
  if (data.kinds.length === 0) return <span className="text-[var(--color-text-muted)]"> · brak formularzy</span>;
  const gaps = data.kinds.reduce((n, k) => n + k.empty.length + k.missing.length, 0);
  if (!gaps) return <span className="text-green-700"> · komplet</span>;
  return <span className="text-amber-700"> · {gaps} do uzupełnienia</span>;
}

// Inline dobór MAPY (formatki) w kroku 4: auto (najświeższa) + wybór ręczny + link
// do regeneracji, gdy jedyna formatka jest nieaktualna / bez scalonego PDF.
function MapPickerPanel({ parcelId, projectId, onDone }: { parcelId: string; projectId?: string; onDone: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["pp", "wpw-map-options", parcelId] as const,
    queryFn: () => getWpwMapOptions(parcelId),
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const run = async (body: { sheet_set_id?: string; auto?: boolean; accept_stale?: boolean }) => {
    setBusy(true); setErr("");
    try {
      const res = await attachWpwMap(parcelId, body);
      if (!res.ok) setErr((res as { reason: string }).reason);
      else {
        qc.invalidateQueries({ queryKey: ["pp", "wpw-map-options", parcelId] });
        await qc.invalidateQueries({ queryKey: ["pp", "wpw-mass-overview"] });
        onDone();
      }
    } catch (e) {
      setErr((e as { response?: { data?: { errors?: { detail?: string } } } })?.response?.data?.errors?.detail || "Błąd doboru mapy.");
    } finally { setBusy(false); }
  };
  if (isLoading) return <div className="py-2"><Spinner className="h-4 w-4" /></div>;
  const candidates = data?.candidates ?? [];
  const freshExists = candidates.some((c) => !c.stale && !c.needs_pdf);
  return (
    <div className="space-y-1.5 py-1 text-[11px]">
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={busy || !freshExists} onClick={() => run({ auto: true })}>
          Dobierz automatycznie (najświeższa)
        </Button>
        {projectId && (
          <Link to={`/power-projects/${projectId}?tab=formatki`} className="text-[var(--color-primary)] hover:underline">
            Sekcja Formatki →
          </Link>
        )}
      </div>
      {candidates.length === 0 && (
        <p className="text-[var(--color-text-muted)]">Brak formatek pokrywających działkę — wygeneruj i scal w sekcji Formatki.</p>
      )}
      {candidates.map((c) => (
        <div key={c.sheet_set_id} className="flex items-center justify-between gap-2 border border-[var(--color-border-muted)] px-2 py-1">
          <div>
            <div className="font-medium">{c.name}</div>
            <div className="text-[10px] text-[var(--color-text-muted)]">
              {c.covers_all ? "pokrywa działkę" : `pokrywa: ${c.covered_teryts.join(", ")}`}
              {c.stale && <span className="text-red-600"> · nieaktualna ({c.stale_reason})</span>}
              {c.needs_pdf && <span className="text-amber-700"> · brak scalonego PDF</span>}
            </div>
          </div>
          {c.needs_pdf ? (
            c.zip_url ? <a href={c.zip_url} className="text-[var(--color-primary)] hover:underline shrink-0">Pobierz ZIP</a> : null
          ) : (
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => run({ sheet_set_id: c.sheet_set_id, accept_stale: c.stale })}>
              {c.stale ? "Użyj mimo to" : "Użyj"}
            </Button>
          )}
        </div>
      ))}
      {err && <p className="text-red-600">{err}</p>}
    </div>
  );
}

// Inline dobór WYPISU w kroku 4: przypisz strony istniejącemu wypisowi albo wgraj nowy.
function WypisPickerPanel({ parcelId, onDone }: { parcelId: string; onDone: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["pp", "wpw-wypis-options", parcelId] as const,
    queryFn: () => getWpwWypisOptions(parcelId),
  });
  const [pages, setPages] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const uploadRef = useRef<HTMLInputElement>(null);
  const [newPages, setNewPages] = useState("");
  const done = async () => {
    qc.invalidateQueries({ queryKey: ["pp", "wpw-wypis-options", parcelId] });
    await qc.invalidateQueries({ queryKey: ["pp", "wpw-mass-overview"] });
    onDone();
  };
  const assign = async (wypisId: string, pagesValue: string) => {
    setBusy(true); setErr("");
    try {
      const res = await assignWpwWypis(parcelId, wypisId, (pagesValue ?? "").trim());
      if (!res.ok) setErr((res as { reason: string }).reason); else done();
    } catch (e) {
      setErr((e as { response?: { data?: { errors?: { detail?: string } } } })?.response?.data?.errors?.detail || "Błąd przypisania stron.");
    } finally { setBusy(false); }
  };
  const upload = async (file: File) => {
    setBusy(true); setErr("");
    try {
      const res = await uploadWpwWypis(parcelId, file, newPages.trim());
      if (!res.ok) setErr((res as { reason: string }).reason); else { setNewPages(""); done(); }
    } catch (e) {
      setErr((e as { response?: { data?: { errors?: { detail?: string } } } })?.response?.data?.errors?.detail || "Błąd wgrywania wypisu.");
    } finally { setBusy(false); if (uploadRef.current) uploadRef.current.value = ""; }
  };
  if (isLoading) return <div className="py-2"><Spinner className="h-4 w-4" /></div>;
  const options = data?.options ?? [];
  return (
    <div className="space-y-1.5 py-1 text-[11px]">
      {options.length === 0 && (
        <p className="text-[var(--color-text-muted)]">Brak wypisów w projekcie — wgraj poniżej.</p>
      )}
      {options.map((w) => (
        <div key={w.wypis_id} className="flex items-center gap-2 border border-[var(--color-border-muted)] px-2 py-1">
          <div className="flex-1 min-w-0">
            <div className="truncate font-medium">{w.filename}</div>
            <div className="text-[10px] text-[var(--color-text-muted)]">
              {w.page_count ? `${w.page_count} str.` : "liczba stron ?"}
              {w.pages ? ` · przypisane: ${w.pages}` : " · brak przypisania"}
            </div>
          </div>
          <input
            className="input-base w-20 text-[11px] py-0.5"
            placeholder="np. 1-3"
            value={pages[w.wypis_id] ?? w.pages}
            onChange={(e) => setPages((p) => ({ ...p, [w.wypis_id]: e.target.value }))}
          />
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => assign(w.wypis_id, pages[w.wypis_id] ?? w.pages)}>Przypisz</Button>
        </div>
      ))}
      <div className="flex items-center gap-2 pt-1 border-t border-dashed border-[var(--color-border-muted)]">
        <input className="input-base w-20 text-[11px] py-0.5" placeholder="strony" value={newPages}
               onChange={(e) => setNewPages(e.target.value)} />
        <input ref={uploadRef} type="file" accept="application/pdf" className="hidden"
               onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
        <Button size="sm" disabled={busy} onClick={() => uploadRef.current?.click()}>
          Wgraj nowy wypis (PDF)
        </Button>
      </div>
      {err && <p className="text-red-600">{err}</p>}
    </div>
  );
}

const RETURN_STATUS_LABEL: Record<WpwReturnBatch["status"], string> = {
  uploaded: "Wgrany",
  segmenting: "Analiza (QR + AI)",
  proposed: "Do przeglądu",
  applied: "Zastosowany",
  cancelled: "Anulowany",
};

// Jedna zablokowana pozycja z wyniku zadania masowego (pakiet = działka × właściciel).
// Wynik `mass_prepare_wpw`/`bulk_compile` niesie `blocked[]`, generacja formularzy
// `failed[]` + `skipped[]` — tu sprowadzone do wspólnego kształtu.
export interface TaskBlockedItem {
  teryt: string;
  parcel_id?: string;
  owner?: string;
  number?: string;
  application_id?: string;
  code?: string;
  reason: string;
}

export function blockedItemsOf(result: Record<string, unknown> | null | undefined): TaskBlockedItem[] {
  if (!result) return [];
  const pick = (key: string): Record<string, unknown>[] =>
    Array.isArray(result[key]) ? (result[key] as Record<string, unknown>[]) : [];
  const str = (v: unknown) => (typeof v === "string" ? v : undefined);
  return [...pick("blocked"), ...pick("failed"), ...pick("skipped")]
    .map((raw) => ({
      teryt: str(raw.teryt) ?? "?",
      parcel_id: str(raw.parcel_id),
      owner: str(raw.owner),
      number: str(raw.number),
      application_id: str(raw.application_id),
      code: str(raw.code) ?? (str(raw.kind) ? "forms_failed" : undefined),
      reason: str(raw.reason) ?? str(raw.error) ?? "bez podanego powodu",
    }));
}

// Ostrzeżenia z wyniku zadania (`warnings[]` — dziś: rozbieżność właścicieli z KW).
// Nie pominęły niczego, ale panel zadania ma z nimi ZOSTAĆ, aż człowiek go zamknie.
export function warningItemsOf(result: Record<string, unknown> | null | undefined): TaskBlockedItem[] {
  if (!result || !Array.isArray(result.warnings)) return [];
  const str = (v: unknown) => (typeof v === "string" ? v : undefined);
  return (result.warnings as Record<string, unknown>[]).map((raw) => ({
    teryt: str(raw.teryt) ?? "?",
    parcel_id: str(raw.parcel_id),
    owner: str(raw.owner),
    number: str(raw.number),
    code: str(raw.code),
    reason: str(raw.reason) ?? "bez podanego powodu",
  }));
}

// Na którym kroku kreatora człowiek poprawia daną blokadę ręcznie. Kody z
// wpw_mass.services.WpwBlocked / quality.package_readiness / bramek działki.
export function stepForBlocker(code: string | undefined): StepKey {
  if (!code) return "generuj";
  if (/^(kw_|owners_missing|no_owner|no_field_packages|no_parcel|off_route|wrong_access_mode)/.test(code)) return "pakiety";
  if (/^(scope_|cad_label|form_values_missing|description|no_form_kinds)/.test(code)) return "tresc";
  return "generuj";
}

// Otwarty panel przebiegu (w toku albo zakończony) blokuje przyciski akcji strony.
export type TaskState = { id: string; label: string; finished: boolean };

// Wyszarzony przycisk nie dostaje zdarzeń myszy, więc powód blokady („zamknij panel
// poprzedniego przebiegu") pokazuje OPAKOWANIE: Tooltip na spanie + pointer-events-none
// na dziecku. Bez otwartego panelu jest przezroczyste — natywny title przycisku działa.
export function TaskBusyHint({ task, children }: { task: TaskState | null; children: ReactNode }) {
  if (!task) return <>{children}</>;
  const content = task.finished
    ? `Poprzedni przebieg „${task.label}” jest zakończony — zamknij jego panel, żeby uruchomić kolejne działanie. Opisy wpisane ręcznie zostają.`
    : `Trwa „${task.label}” — poczekaj na koniec albo zamknij panel przebiegu (to anuluje zadanie), żeby odblokować przyciski.`;
  return (
    <Tooltip content={content} className="inline-flex cursor-not-allowed [&>*]:pointer-events-none">
      {children}
    </Tooltip>
  );
}

// Live log of the currently running bulk task (AsyncTaskRecord polling).
// Po błędzie panel ZOSTAJE (zamyka go użytkownik): pod logiem lista zablokowanych
// działek/pakietów z powodem i skokiem do wiersza, w którym poprawia się je ręcznie.
// „Zamknij" w trakcie pracy = anulowanie zadania (bg-tasks cancel: revoke + flaga, którą
// pętle zadań masowych sprawdzają) — po nieudanych opisach AI człowiek wpisuje je ręcznie
// i chce od razu generować pakiety, zamiast czekać na koniec przebiegu (zgłoszenie 17.09.2026).
export function TaskLogPanel({ taskId, label, onDone, onClose, onLocate }: {
  taskId: string;
  label: string;
  /** `partial` = zadanie skończyło się sukcesem, ale część pozycji pominięto (lista pod logiem). */
  onDone: (status: string, partial: boolean) => void;
  /** `cancelled` = zamknięto w trakcie pracy (zadanie anulowane) — dane mogły się zmienić częściowo. */
  onClose: (cancelled: boolean) => void;
  onLocate: (item: TaskBlockedItem) => void;
}) {
  const task = useTaskPolling(taskId);
  const cancel = useMutation({
    mutationFn: () => bgTasksApi.cancel(taskId),
    // 400 „nie jest już aktywne" = skończyło się między odpytaniami; panel i tak zamykamy,
    // a odświeżenie po stronie strony pokaże, co zdążyło powstać.
    onSettled: () => onClose(true),
  });
  const isDeveloper = useAuthStore((s) => !!s.user?.is_developer);
  const status = task.data?.status;
  // Zadanie masowe kończy się BŁĘDEM tylko, gdy nic się nie udało; „sukces z pominięciami"
  // (część pakietów zablokowana) niesie listę w wyniku — panel ma wtedy ZOSTAĆ i pokazać
  // „Do poprawienia ręcznie", zamiast znikać po 4 s jak przy czystym sukcesie.
  const result = (task.data?.result ?? null) as Record<string, unknown> | null;
  const blocked = useMemo(() => blockedItemsOf(result), [result]);
  const warnings = useMemo(() => warningItemsOf(result), [result]);
  const partial = status === "success" && blocked.length > 0;
  // Sukces z ostrzeżeniami (np. właściciele niezgodni z KW): nic nie pominięto, ale
  // panel nie może zniknąć po 4 s — zostaje, aż użytkownik go zamknie.
  const warned = status === "success" && warnings.length > 0;
  const doneRef = useRef(false);
  useEffect(() => {
    if (!doneRef.current && status && ["success", "failure", "cancelled"].includes(status)) {
      doneRef.current = true;
      onDone(status, partial || warned);
    }
  }, [status, partial, warned, onDone]);
  const logRef = useRef<HTMLPreElement>(null);
  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [task.data?.log]);
  const devErrorCount = Array.isArray(result?.dev_errors) ? (result.dev_errors as unknown[]).length : 0;
  const finished = !!status && ["success", "failure", "cancelled"].includes(status);
  return (
    <div className="border border-[var(--color-border-strong)] bg-[var(--color-bg-elevated)] p-2 space-y-1">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-mono uppercase tracking-wide flex items-center gap-2">
          {status && ["pending", "running"].includes(status) && <Spinner className="h-3.5 w-3.5" />}
          {label} — {partial ? `zakończono z pominięciami (${blocked.length})` : warned ? `zakończono z ostrzeżeniami (${warnings.length})` : status === "success" ? "zakończono" : status === "failure" ? "błąd" : status === "cancelled" ? "anulowano" : "w toku…"}
          {(partial || warned) && <AlertTriangle size={13} className="text-amber-600" />}
        </p>
        {finished ? (
          <Button size="sm" onClick={() => onClose(false)} data-testid="task-panel-close"
            title="Zamknij panel przebiegu i odblokuj przyciski akcji">
            <X size={12} /> Zamknij
          </Button>
        ) : (
          <Button size="sm" onClick={() => cancel.mutate()} loading={cancel.isPending} data-testid="task-panel-cancel"
            title="Przerwij zadanie (to, co już powstało, zostaje) i zamknij panel">
            <Ban size={12} /> Anuluj i zamknij
          </Button>
        )}
      </div>
      <pre ref={logRef} className="text-[11px] whitespace-pre-wrap max-h-48 overflow-y-auto text-[var(--color-text-secondary)]">
        {task.data?.log || "…"}
      </pre>
      {status === "failure" && task.data?.error && (
        <p className="text-xs text-red-600 whitespace-pre-wrap">{task.data.error}</p>
      )}
      {finished && blocked.length > 0 && (
        <div className="mt-1 border-t border-[var(--color-border-muted)] pt-1.5">
          <p className="text-[10px] font-mono uppercase tracking-wide text-[var(--color-text-muted)] mb-1">
            Do poprawienia ręcznie ({blocked.length}){partial && " — reszta pozycji gotowa"}
          </p>
          <ul className="space-y-0.5">
            {blocked.map((item, i) => (
              <li key={`${i}:${item.teryt}:${item.number ?? item.owner ?? ""}:${item.code ?? ""}`}
                className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[11px]">
                <span className="font-mono font-semibold text-[var(--color-text-primary)]">{item.teryt}</span>
                {(item.number || item.owner) && (
                  <span className="text-[var(--color-text-muted)]">
                    {[item.number, item.owner].filter(Boolean).join(" · ")}
                  </span>
                )}
                <span className="text-red-700 dark:text-red-300">{item.reason}</span>
                {item.code && <span className="font-mono text-[10px] text-[var(--color-text-muted)]">[{item.code}]</span>}
                <button type="button" onClick={() => onLocate(item)}
                  className="text-[var(--color-primary)] hover:underline"
                  title="Przejdź do kroku i wiersza tej działki, żeby poprawić ją ręcznie">
                  popraw ręcznie →
                </button>
                {item.application_id && (
                  <Link to={`/power-projects/wnioski/${item.application_id}`} target="_blank"
                    className="text-[var(--color-primary)] hover:underline" title="Otwórz WNU w nowej karcie">
                    otwórz WNU ↗
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {finished && warnings.length > 0 && (
        <div className="mt-1 border-t border-amber-200 dark:border-amber-500/20 pt-1.5" data-testid="task-warnings">
          <p className="text-[10px] font-mono uppercase tracking-wide text-amber-700 dark:text-amber-400 mb-1">
            Ostrzeżenia ({warnings.length}) — pakiety powstały, sprawdź przed wysłaniem w teren
          </p>
          <ul className="space-y-0.5">
            {warnings.map((item, i) => (
              <li key={`${i}:${item.teryt}:${item.code ?? ""}`}
                className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[11px]">
                <span className="font-mono font-semibold text-[var(--color-text-primary)]">{item.teryt}</span>
                <span className="text-amber-800 dark:text-amber-200">{item.reason}</span>
                {item.code && <span className="font-mono text-[10px] text-[var(--color-text-muted)]">[{item.code}]</span>}
                <button type="button" onClick={() => onLocate(item)}
                  className="text-[var(--color-primary)] hover:underline"
                  title="Przejdź do wiersza tej działki">
                  pokaż działkę →
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {isDeveloper && (status === "failure" || partial) && (
        <p className="text-[10px] text-[var(--color-text-muted)]">
          DEV: pełny log workera Celery (tracebacki, ponowienia modelu) —{" "}
          <Link to={`/dev/bg-tasks?task=${taskId}`} target="_blank" className="underline">
            Zadania automatyczne → „Log workera"
          </Link>
          {devErrorCount > 0 && <> · {devErrorCount} tracebacków w „Szczegóły techniczne"</>}
        </p>
      )}
    </div>
  );
}

/**
 * Dodatkowe strony A4 pakietu (krok „Generuj i kompiluj"): dowolna liczba PDF-ów
 * doklejanych do egzemplarza właściciela ZA formularzami, PRZED mapą (formatką A3).
 * Każdy plik ma własne przypisanie do działek — domyślnie wszystkie działki sekwencji
 * (także dodane później), albo jawna lista. Kolejność w pakiecie = kolejność na liście.
 * Zmiana listy unieważnia sprawdzone kompilacje — pakiety trzeba złożyć ponownie.
 */
// Pakiet niesie DWA komplety formularzy z mapą: pierwszy zostaje u właściciela, drugi
// („egzemplarz WPW") wraca podpisany do zbierającego. Strona ląduje na KOŃCU sekcji A4
// wybranej części — za formularzami, przed formatką A3.
const EXTRA_PLACEMENT_OPTIONS: { value: WpwExtraPagePlacement; label: string; hint: string }[] = [
  {
    value: "owner",
    label: "w sekcji właściciela",
    hint: "Strona wchodzi na koniec sekcji A4 egzemplarza, który ZOSTAJE U WŁAŚCICIELA "
      + "(za formularzami, przed mapą) — np. klauzula RODO albo instrukcja odesłania.",
  },
  {
    value: "collector",
    label: "w części dla zbierającego",
    hint: "Strona wchodzi na koniec sekcji A4 egzemplarza WRACAJĄCEGO do zbierającego "
      + "(egzemplarz WPW) — np. oświadczenie, które ma wrócić podpisane razem z pakietem.",
  },
  {
    value: "both",
    label: "w obu częściach",
    hint: "Strona wchodzi do obu egzemplarzy — właściciel zostaje ze swoją kopią, druga "
      + "wraca podpisana do zbierającego.",
  },
];

export function ExtraPagesPanel({ ctx, parcels, canMutate }: {
  ctx: WpwMassCtx | null;
  parcels: { id: string; label: string }[];
  canMutate: boolean;
}) {
  const { data, isLoading } = useWpwMassExtraFiles(ctx);
  const m = useWpwMassExtraFileMutations(ctx);
  // Link podpisany w chwili kliknięcia — patrz utils/managedFile.ts.
  const { openFile } = useManagedFileOpener();
  const inputRef = useRef<HTMLInputElement>(null);
  const files = data?.files ?? [];
  const busy = m.upload.isPending || m.patch.isPending || m.remove.isPending;
  const error = [m.upload.error, m.patch.error, m.remove.error].map((e) => (e ? extractApiError(e) : null)).find(Boolean);
  const parcelLabel = (id: string) => parcels.find((p) => p.id === id)?.label ?? id.slice(0, 8);

  const onPick = (list: FileList | null) => {
    const picked = Array.from(list ?? []);
    if (picked.length === 0) return;
    m.upload.mutate({ files: picked });
    if (inputRef.current) inputRef.current.value = "";
  };
  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= files.length) return;
    m.patch.mutate({ id: files[index].id, position: target });
  };

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] shadow-sm overflow-hidden"
         data-testid="extra-pages-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-[var(--color-border-muted)]">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm text-[var(--color-text-primary)]">
            <FileText size={14} />
            <b>Dodatkowe strony A4</b>
            <span className="text-[var(--color-text-muted)]">{files.length > 0 ? `(${files.length})` : ""}</span>
          </div>
          <p className="text-[11px] text-[var(--color-text-secondary)] mt-0.5">
            Wchodzą <b>na koniec sekcji A4</b> — za formularzami, przed mapą (formatką A3): np. pismo
            przewodnie, klauzula RODO, instrukcja odesłania. Przy każdym pliku wskazujesz <b>część
            pakietu</b>: egzemplarz zostający u właściciela, egzemplarz wracający do zbierającego, albo
            oba. Plik możesz też przypiąć do wybranych działek — domyślnie trafia do wszystkich.
            Po zmianie listy złóż pakiety ponownie.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={inputRef} type="file" accept="application/pdf,.pdf" multiple className="hidden"
                 data-testid="extra-pages-input"
                 onChange={(e) => onPick(e.target.files)} />
          <Button size="sm" variant="secondary" disabled={!canMutate || busy || !ctx} loading={m.upload.isPending}
                  onClick={() => inputRef.current?.click()}
                  title="Wgraj jeden lub więcej PDF-ów — każdy stanie się osobną sekcją pakietu (domyślnie dla wszystkich działek)">
            <Upload size={13} className="mr-1" /> Dodaj pliki PDF
          </Button>
        </div>
      </div>
      {error && (
        <p className="px-4 py-2 text-[11px] text-red-700 dark:text-red-300 border-b border-[var(--color-border-muted)]">{error}</p>
      )}
      {isLoading && <div className="px-4 py-3"><Spinner /></div>}
      {!isLoading && files.length === 0 && (
        <p className="px-4 py-3 text-[11px] text-[var(--color-text-muted)]">
          Brak dodatkowych stron — pakiet to okładka QR, formularze, mapa, wypis i egzemplarz WPW.
        </p>
      )}
      {files.length > 0 && (
        <ul className="divide-y divide-[var(--color-border-muted)]">
          {files.map((f, index) => {
            const chosen = new Set(f.parcel_ids);
            return (
              <li key={f.id} className="px-4 py-2.5 space-y-1.5" data-testid="extra-page-row">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] font-mono text-[var(--color-text-muted)] w-6">#{index + 1}</span>
                  <input
                    className="input-base text-[12px] py-0.5 flex-1 min-w-[12rem]"
                    defaultValue={f.label}
                    key={`${f.id}:${f.label}`}
                    disabled={!canMutate || busy}
                    title="Etykieta w spisie treści okładki („Załącznik — …”); domyślnie nazwa pliku"
                    aria-label={`Etykieta pliku ${f.label}`}
                    onBlur={(e) => {
                      const next = e.target.value.trim();
                      if (next && next !== f.label) m.patch.mutate({ id: f.id, label: next });
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                  />
                  <span className="text-[11px] text-[var(--color-text-muted)] whitespace-nowrap">
                    {f.page_count != null ? `${f.page_count} ${plural(f.page_count, "strona", "strony", "stron")}` : ""}
                    {f.uploaded_by ? ` · ${f.uploaded_by}` : ""}
                  </span>
                  {f.file?.url && (
                    <a href={f.file.url} target="_blank" rel="noreferrer"
                       onClick={(e) => { if (!isPlainLeftClick(e)) return; e.preventDefault(); void openFile(f.file!); }}
                       className="inline-flex items-center gap-1 text-[11px] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                       title={f.file.original_filename}>
                      <Download size={12} /> PDF
                    </a>
                  )}
                  <span className="inline-flex items-center gap-0.5 ml-auto">
                    <button type="button" className="p-1 rounded hover:bg-[var(--color-bg-elevated)] disabled:opacity-40"
                            disabled={!canMutate || busy || index === 0} onClick={() => move(index, -1)}
                            title="Wyżej (wcześniej w pakiecie)" aria-label="Wyżej">
                      <ArrowUp size={13} />
                    </button>
                    <button type="button" className="p-1 rounded hover:bg-[var(--color-bg-elevated)] disabled:opacity-40"
                            disabled={!canMutate || busy || index === files.length - 1} onClick={() => move(index, 1)}
                            title="Niżej (później w pakiecie)" aria-label="Niżej">
                      <ArrowDown size={13} />
                    </button>
                    <button type="button" className="p-1 rounded text-red-700 dark:text-red-300 hover:bg-[var(--color-bg-elevated)] disabled:opacity-40"
                            disabled={!canMutate || busy}
                            onClick={() => { if (window.confirm(`Usunąć „${f.label}” z pakietów?`)) m.remove.mutate(f.id); }}
                            title="Usuń z pakietów (plik zostaje w magazynie)" aria-label={`Usuń ${f.label}`}>
                      <Trash2 size={13} />
                    </button>
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-8 text-[11px]">
                  {/* w KTÓREJ części pakietu ląduje strona — obok wyboru, w KTÓRYCH działkach */}
                  <label className="inline-flex items-center gap-1.5 text-[var(--color-text-secondary)]"
                         title={EXTRA_PLACEMENT_OPTIONS.find((o) => o.value === f.placement)?.hint}>
                    dokładaj:
                    <select className="input-base text-[11px] py-0.5 px-1"
                            value={f.placement} disabled={!canMutate || busy}
                            aria-label={`Część pakietu: ${f.label}`}
                            onChange={(e) => m.patch.mutate({ id: f.id, placement: e.target.value as WpwExtraPagePlacement })}>
                      {EXTRA_PLACEMENT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value} title={o.hint}>{o.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer select-none text-[var(--color-text-secondary)]"
                         title="Plik wchodzi do pakietów WSZYSTKICH działek sekwencji — także dodanych później">
                    <input type="checkbox" checked={f.all_parcels} disabled={!canMutate || busy}
                           aria-label={`Wszystkie działki: ${f.label}`}
                           onChange={(e) => m.patch.mutate(e.target.checked
                             ? { id: f.id, all_parcels: true }
                             // start od kompletu — użytkownik odznacza te, do których plik nie idzie
                             : { id: f.id, all_parcels: false, parcel_ids: parcels.map((p) => p.id) })} />
                    wszystkie działki ({parcels.length})
                  </label>
                  {!f.all_parcels && (
                    <span className="flex flex-wrap items-center gap-1">
                      {parcels.map((p) => {
                        const on = chosen.has(p.id);
                        return (
                          <label key={p.id}
                                 className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 cursor-pointer select-none ${
                                   on ? "border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)]"
                                      : "border-[var(--color-border-muted)] text-[var(--color-text-muted)]"}`}
                                 title={on && chosen.size === 1 ? "Ostatnia działka — zamiast odznaczać, usuń plik albo zaznacz „wszystkie”" : p.label}>
                            <input type="checkbox" checked={on} disabled={!canMutate || busy || (on && chosen.size === 1)}
                                   aria-label={`${p.label}: ${f.label}`}
                                   onChange={(e) => {
                                     const next = new Set(chosen);
                                     if (e.target.checked) next.add(p.id); else next.delete(p.id);
                                     m.patch.mutate({ id: f.id, parcel_ids: [...next] });
                                   }} />
                            {p.label}
                          </label>
                        );
                      })}
                      {/* działki spoza bieżącego zbioru (np. odpięte od trasy) — pokazujemy, żeby nic nie znikało po cichu */}
                      {f.parcel_ids.filter((id) => !parcels.some((p) => p.id === id)).map((id) => (
                        <span key={id} className="rounded border border-dashed border-[var(--color-border-muted)] px-1.5 py-0.5 text-[var(--color-text-muted)]"
                              title="Działka spoza bieżącej listy sekwencji">
                          {parcelLabel(id)}
                        </span>
                      ))}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function RowDescriptionEditor({
  row, disabled, onSave, onGenerate, generating, aside,
}: {
  row: WpwMassRow;
  disabled: boolean;
  /** Zapis do bazy — obietnica, żeby przycisk „Zapisz” pokazał spinner i błąd serwera. */
  onSave: (value: Record<string, string>) => Promise<unknown>;
  onGenerate: () => void;
  generating: boolean;
  /** Dodatkowe przyciski strony (np. „pola szablonów”) — lądują w kolumnie OBOK pola. */
  aside?: ReactNode;
}) {
  // Jeden opis na KAŻDY status dobranego formularza (projektowane/likwidowane/modernizowane)
  // — szablon każdego formularza WPW bierze kubełek swojego statusu.
  const saved = row.parcel.consent_work_descriptions || {};
  const statuses = opisStatusesOf(row);
  const [values, setValues] = useState<Record<string, string>>(saved);
  // Stan zapisu przycisku „Zapisz”: po udanym zapisie pamiętamy, CO poszło do bazy
  // (justSaved), bo overview odświeża się dopiero po chwili — bez tego przycisk
  // wracałby na ułamek sekundy po kliknięciu. Backend trimuje i wyrzuca puste kubełki,
  // więc porównujemy tak samo — dawniej końcowy enter/spacja trzymały odznakę
  // „niezapisane” na stałe mimo udanego zapisu.
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState<Record<string, string> | null>(null);
  const baseline = justSaved ?? saved;
  const dirty = statuses.some((s) => (values[s] || "").trim() !== (baseline[s] || "").trim());
  // Migawka serwera, z której pole OSTATNIO wzięło treść. Gdy overview odświeża się
  // w tle (opis właśnie ułożony przez AI — „Generuj AI” w wierszu, krok „Opisy
  // urządzeń” albo sekwencja masowa), przejmujemy z serwera każdy kubełek, którego
  // użytkownik od ostatniej synchronizacji NIE dotknął; jego niezapisane wpisy zostają.
  // Dawniej „niezapisana edycja” była liczona względem ŚWIEŻEGO stanu serwera, więc
  // opis, który dopiero co przyszedł z AI, wyglądał jak lokalna edycja: pole zostawało
  // puste (aż do F5) z odznaką „niezapisane”, a kliknięcie poza nie zapisywało tę
  // pustkę do bazy — formularze i pakiet wychodziły bez opisu (zgłoszenie 04.09.2026).
  const syncedRef = useRef<Record<string, string>>(saved);
  useEffect(() => {
    const fresh = row.parcel.consent_work_descriptions || {};
    const base = syncedRef.current;
    syncedRef.current = fresh;
    setJustSaved(null);
    setValues((cur) => {
      const next: Record<string, string> = { ...fresh };
      for (const k of new Set([...Object.keys(cur), ...Object.keys(base)])) {
        if ((cur[k] || "").trim() !== (base[k] || "").trim()) next[k] = cur[k] || "";
      }
      return next;
    });
  }, [row.parcel.consent_work_descriptions]);
  const save = async () => {
    if (!dirty || saving) return;
    const merged: Record<string, string> = {};
    statuses.forEach((s) => {
      const v = (values[s] || "").trim();
      if (v) merged[s] = v;
    });
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(merged);
      // Od teraz overview ma te wartości — kolejne odświeżenie w tle nie ma ich
      // traktować jak lokalnej edycji użytkownika.
      syncedRef.current = { ...syncedRef.current, ...merged };
      setJustSaved(merged);
    } catch (err) {
      setSaveError(extractApiError(err) || "Nie udało się zapisać opisu.");
    } finally {
      setSaving(false);
    }
  };
  // Układ: pole tekstowe zajmuje całą szerokość kolumny, a przyciski i podpowiedzi
  // stoją w wąskiej kolumnie OBOK niego — pod polem nic nie zabiera miejsca w pionie.
  return (
    <div className="flex items-start gap-3">
      <div className="flex-1 min-w-0 space-y-1">
        {statuses.length === 0 && (
          <p className="text-[10px] text-[var(--color-text-muted)]">
            Brak dobranego formularza — najpierw zaznacz rodzaj (kolumna Formularze).
          </p>
        )}
        {/* Opis jest cechą działki, a wiersz — właściciela: przy współwłasności ta sama
            treść stoi w kilku wierszach i zapis w jednym z nich zmienia ją we wszystkich. */}
        {statuses.length > 0 && (row.owners?.length ?? 0) > 1 && (
          <p className="text-[10px] text-[var(--color-text-muted)]">
            Opis wspólny dla działki — zapis (ręczny albo AI) pojawi się u wszystkich
            współwłaścicieli ({row.owners.length}).
          </p>
        )}
        {statuses.map((s) => (
          <div key={s} className="space-y-0.5">
            <span className="inline-flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-wide text-[var(--color-text-muted)]">
              {statuses.length > 1 && <span>{s}</span>}
              {/* Skąd jest ten opis: ręczny zostaje nietknięty przez „Przygotuj i sprawdź". */}
              {(row.parcel.consent_work_descriptions?.[s] || "").trim() && (
                <span title={row.parcel.consent_description_sources?.[s] === "manual"
                  ? "Wpisany ręcznie — „Przygotuj i sprawdź” go nie nadpisze"
                  : row.parcel.consent_description_sources?.[s]
                    ? "Ułożony przez AI — „Przygotuj i sprawdź” może go poprawić"
                    : "Pochodzenie nieznane — traktowany jak ręczny (sekwencja nie nadpisze)"}
                  className="normal-case tracking-normal rounded border border-[var(--color-border)] px-1">
                  {row.parcel.consent_description_sources?.[s] === "ai" || row.parcel.consent_description_sources?.[s] === "review"
                    ? "AI" : "ręczny"}
                </span>
              )}
            </span>
            <textarea
              className="input-base w-full text-[11px] leading-snug py-1 min-h-[132px]"
              placeholder="…urządzeń elektroenergetycznych w postaci: (dopełniacz, bez czasownika)"
              value={values[s] || ""}
              disabled={disabled}
              onChange={(e) => setValues((v) => ({ ...v, [s]: e.target.value }))}
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === "Enter") { e.preventDefault(); void save(); }
              }}
            />
          </div>
        ))}
      </div>
      <div className="w-40 shrink-0 flex flex-col items-start gap-1.5 pt-0.5">
        <button
          type="button"
          disabled={disabled || generating}
          onClick={onGenerate}
          className="inline-flex items-center gap-1 text-[11px] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] disabled:opacity-50"
          title="Wygeneruj opisy AI per status z elementów mapy na działce — przelicza WSZYSTKIE kubełki i nadpisze także opisy wpisane ręcznie (zapyta o potwierdzenie)"
        >
          {generating ? <Spinner className="h-3 w-3" /> : <Sparkles size={12} />} Generuj AI
        </button>
        {aside}
        {(dirty || saving) && (
          <Button
            type="button"
            size="sm"
            loading={saving}
            disabled={disabled}
            onClick={() => void save()}
            title="Zapisz ręczny opis do bazy (Ctrl+Enter w polu)"
          >
            {saving ? "Zapisywanie…" : "Zapisz"}
          </Button>
        )}
        {saveError && (
          <p className="text-[10px] leading-snug text-red-600 border-l-2 border-red-400 pl-1.5">
            Nie zapisano: {saveError}
          </p>
        )}
        {row.breakdown.unclassified.length > 0 && statuses.length > 0 && (
          <p className="text-[10px] leading-snug text-amber-700 border-l-2 border-amber-400 pl-1.5">
            ℹ Urządzenia bez określonego statusu na mapie przyjęto jako{" "}
            <span className="font-medium">projektowane</span>:{" "}
            {row.breakdown.unclassified.join(", ")}. Jeśli to likwidacja/modernizacja —
            zmień rodzaj formularza (kolumna Formularze).
          </p>
        )}
      </div>
    </div>
  );
}

// Blokada DZIAŁKI, której kreator sam nie zdejmie: analiza KW, wątpliwości zakresu
// z etykiet CAD albo brak właściciela. Taka działka nie wchodzi do domyślnego
// zaznaczenia kroku 1 (zgłoszenie 08.09.2026: „nie wybrałem działek, a w kolejnych
// krokach są i mają błędy") i jest wypisana nad tabelą kroków 3/4, jeśli ktoś ją
// zaznaczył świadomie. Zwraca powód albo null.
export function parcelBlockReason(row: WpwMassRow): string | null {
  const parcel = row.parcel;
  if (parcel.consent?.kw_gate?.blocker) return parcel.consent.kw_gate.blocker.reason;
  if (parcel.scope_review?.needed) {
    return `zakres z etykiet CAD do zatwierdzenia (${parcel.scope_review.doubts.length})`;
  }
  const owners = row.owners?.length ? row.owners : (row.owner ? [row.owner] : []);
  if (owners.length === 0) return "brak właściciela — dodaj adresata WPW w danych działki";
  return null;
}

// Ostrzeżenie DZIAŁKI, które nie blokuje (dziś: właściciele niezgodni z KW). Przed
// generacją/kompilacją kreator pokazuje je w modalu do potwierdzenia — zamiast błędu,
// który zatrzymywał druk (zgłoszenie 11.09.2026). Zwraca powód albo null.
export function parcelWarningReason(row: WpwMassRow): string | null {
  const gate = row.parcel.consent?.kw_gate;
  if (gate?.warning && !gate.blocker) return gate.warning.reason;
  return null;
}

// Blokada DZIAŁKI, którą wolno pominąć z potwierdzeniem (dziś: analiza KW nie odczytała
// właścicieli z Działu II). Modal przed generacją/kompilacją proponuje „pomiń błąd” —
// zwolnienie zapisuje się na działce (kto, kiedy, powód od systemu) i da się cofnąć.
// Zwraca powód albo null.
export function parcelSkippableBlockReason(row: WpwMassRow): string | null {
  const blocker = row.parcel.consent?.kw_gate?.blocker;
  return blocker?.skippable ? blocker.reason : null;
}

// Modal „ostrzeżenie do potwierdzenia": nie zamyka się sam ani po kliknięciu obok —
// tylko „Kontynuuj mimo to" (start zadania) albo „Anuluj" / Escape. Druga sekcja —
// blokady POMIJALNE (`skippable`): checkbox „pomiń błąd i generuj” zdejmuje je przed
// startem (onConfirm dostaje listę id działek do zwolnienia; pusta = zostają zablokowane).
// `items[].row` to wiersz kreatora albo — ze strony pojedynczego pakietu — sam
// minimalny opis działki (id, TERYT), stąd typ strukturalny zamiast WpwMassRow.
export type KwWarningItem = {
  row: { parcel: { id: string; teryt: string; short_number?: string | null } };
  reason: string;
};
function KwParcelList({ items, tone }: { items: KwWarningItem[]; tone: "amber" | "red" }) {
  return (
    <ul className="max-h-72 overflow-y-auto divide-y divide-[var(--color-border-muted)] border border-[var(--color-border-muted)]">
      {items.map(({ row, reason }) => (
        <li key={row.parcel.id} className="px-3 py-1.5 text-[11px]">
          <span className="font-mono font-semibold text-[var(--color-text-primary)]">
            {row.parcel.short_number || row.parcel.teryt}
          </span>
          {row.parcel.short_number && (
            <span className="ml-1 font-mono text-[10px] text-[var(--color-text-muted)]">{row.parcel.teryt}</span>
          )}
          <p className={`mt-0.5 ${tone === "red" ? "text-red-700 dark:text-red-300" : "text-amber-800 dark:text-amber-200"}`}>
            {reason}
          </p>
        </li>
      ))}
    </ul>
  );
}

export function KwWarningConfirmModal({
  items, skippable = [], actionLabel, onConfirm, onCancel, busy = false, error = null,
}: {
  items: KwWarningItem[];
  /** Blokady pomijalne (błąd analizy KW) — pusta lista = tylko ostrzeżenia. */
  skippable?: KwWarningItem[];
  actionLabel: string;
  /** `skipParcelIds` — działki, na których zdjąć blokadę przed startem (pusta = żadnej). */
  onConfirm: (skipParcelIds: string[]) => void;
  onCancel: () => void;
  busy?: boolean;
  error?: string | null;
}) {
  const [skip, setSkip] = useState(false);
  const skipIds = skip ? skippable.map(({ row }) => row.parcel.id) : [];
  const title = items.length === 0
    ? "Błąd analizy KW — pominąć?"
    : skippable.length === 0 ? "Właściciele niezgodni z KW" : "Ostrzeżenia i błędy analizy KW";
  return (
    <Modal open title={title} onClose={busy ? () => undefined : onCancel} size="lg">
      <div className="space-y-3" data-testid="kw-warning-confirm">
        {items.length > 0 && (
          <>
            <p className="text-xs text-[var(--color-text-secondary)] flex items-start gap-2">
              <AlertTriangle size={16} className="shrink-0 text-amber-600 dark:text-amber-400" />
              <span>
                {items.length === 1 ? "1 zaznaczona działka ma" : `${items.length} zaznaczonych działek ma`} właścicieli
                innych niż w Dziale II księgi wieczystej. Pakiety powstaną dla właścicieli wpisanych na działce —
                zgoda podpisana przez osobę, która wg księgi nie jest właścicielem, jest bezwartościowa.
                Sprawdź listę, zanim pójdzie w teren.
              </span>
            </p>
            <KwParcelList items={items} tone="amber" />
          </>
        )}
        {skippable.length > 0 && (
          <div className="space-y-2" data-testid="kw-skippable">
            <p className="text-xs text-[var(--color-text-secondary)] flex items-start gap-2">
              <XCircle size={16} className="shrink-0 text-red-600 dark:text-red-400" />
              <span>
                {skippable.length === 1 ? "1 zaznaczona działka ma" : `${skippable.length} zaznaczonych działek ma`} błąd
                analizy KW: analiza nie odczytała właścicieli z Działu II (urwana treść albo zły dokument).
                Bez pominięcia błędu pakiety tych działek NIE powstaną — reszta zaznaczenia przejdzie.
                Najlepiej pobrać księgę ponownie i przeanalizować; jeśli to niemożliwe, pomiń błąd:
                zgody pójdą do właścicieli wpisanych na działce bez sprawdzenia z księgą, a zwolnienie
                zapisze się na działce z Twoim nazwiskiem (da się cofnąć w kroku „WNU”).
              </span>
            </p>
            <KwParcelList items={skippable} tone="red" />
            <label className="inline-flex items-start gap-2 text-xs text-[var(--color-text-primary)] cursor-pointer select-none">
              <input type="checkbox" checked={skip} disabled={busy} onChange={(e) => setSkip(e.target.checked)}
                data-testid="kw-skip-checkbox" className="mt-0.5" />
              <span>
                Pomiń błąd analizy KW i wygeneruj WPW dla {skippable.length === 1 ? "tej działki" : "tych działek"}
                {" "}(potwierdzam, że biorę to na siebie)
              </span>
            </label>
          </div>
        )}
        {error && <p className="text-xs text-red-600 whitespace-pre-wrap">{error}</p>}
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>Anuluj</Button>
          <Button size="sm" onClick={() => onConfirm(skipIds)} loading={busy} data-testid="kw-warning-proceed">
            {skip ? `Pomiń błąd i ${actionLabel}` : `Rozumiem — ${actionLabel}`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function WpwMassPackagePage() {
  const { id: projectId = "", orderId = "" } = useParams();
  // Tryb: zlecenie (:orderId) albo — sekwencja masowa bez zlecenia — projekt.
  // Zamówienie powstaje wtedy dopiero w kroku 5 (formularz zapotrzebowania).
  const { data: connectionForSeq } = useConnectionByProject(orderId ? undefined : projectId);
  const ctx: WpwMassCtx | null = orderId
    ? { order: orderId }
    : connectionForSeq?.id ? { connection: connectionForSeq.id } : null;
  const qc = useQueryClient();
  // Pliki sekwencji (formularze, ODT, skompilowane pakiety) pobierają się przez link
  // podpisany W CHWILI KLIKNIĘCIA: ta strona wisi otwarta godzinami, a presigned S3
  // z payloadu umiera po godzinie. Patrz utils/managedFile.ts.
  const { openFile } = useManagedFileOpener();
  const { data, isLoading, refetch } = useWpwMassOverview(ctx);
  const m = useWpwMassMutations(ctx);
  const rm = useWpwReturnMutations();
  const { data: batches } = useWpwReturnBatches({ order: orderId }, !!orderId);
  const isSequence = data?.mode === "connection";
  const { data: seqParcels } = useParcels(isSequence ? data?.connection_id : undefined);

  // Krok kreatora (tylko tryb sekwencji) — trzymany w URL, żeby odświeżenie nie gubiło miejsca.
  const [searchParams, setSearchParams] = useSearchParams();
  const step = (STEPS.find((s) => s.key === searchParams.get("krok"))?.key ?? "dzialki") as StepKey;
  const goStep = (k: StepKey) => setSearchParams((p) => { p.set("krok", k); return p; }, { replace: true });
  const stepIdx = STEPS.findIndex((s) => s.key === step);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [task, setTask] = useState<TaskState | null>(null);
  // Działka wskazana z listy „Do poprawienia ręcznie" pod logiem zadania: po zmianie
  // kroku wiersz jest przewijany na ekran i podświetlany przez chwilę.
  const [highlightParcel, setHighlightParcel] = useState<string | null>(null);
  // Błąd wgrania skanu zwrotu (zły typ pliku / 400 z serwera) — dotąd znikał po cichu.
  const [returnUploadError, setReturnUploadError] = useState("");
  const [rowAiParcel, setRowAiParcel] = useState<string | null>(null);
  const [fieldsParcel, setFieldsParcel] = useState<string | null>(null);
  const [mapPicker, setMapPicker] = useState<string | null>(null);
  const [wypisPicker, setWypisPicker] = useState<string | null>(null);
  const [reviewBatch, setReviewBatch] = useState<string | null>(null);
  // Okno księgi wieczystej otwierane wprost z bramki KW — to samo, co w zakładce Działki.
  const [kwParcel, setKwParcel] = useState<{ id: string; teryt: string; kw_number?: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const odtRef = useRef<HTMLInputElement>(null);
  const [odtTarget, setOdtTarget] = useState<string | null>(null);
  const [odtBusy, setOdtBusy] = useState(false);
  // Wynik ostatniego wgrania poprawionego ODT — błąd żądania albo nieudana konwersja
  // do PDF. Bez tego wgranie „nie robiło nic", a pakiet dalej szedł ze starą treścią.
  const [odtMsg, setOdtMsg] = useState<{ kind: "error" | "ok"; text: string } | null>(null);
  // Edytor formularza w przeglądarce (Collabora na pełnym ekranie) — slot = id wersji formularza.
  // Sesja edytora online: formularz pakietu (wpw_form) albo część listu (pismo / kopia formularza).
  const [editorForm, setEditorForm] = useState<{ id: string; label: string; kind?: EditorDocKind } | null>(null);
  const { data: editorAvailability } = useEditorAvailability();
  const editorAvailable = !!editorAvailability?.available;
  const [duplexPrint, setDuplexPrint] = useState(false);

  // Wiersze overview są per (działka, WŁAŚCICIEL). Selekcja i krok 1 działają na DZIAŁKACH,
  // więc `parcelRows` = po jednym reprezentancie na działkę; praca kroków 3/4 idzie po
  // wierszach właścicieli zaznaczonych działek.
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const parcelRows = useMemo(() => {
    const seen = new Set<string>();
    return rows.filter((r) => (seen.has(r.parcel.id) ? false : seen.add(r.parcel.id)));
  }, [rows]);
  // Działki do przypinania dodatkowych stron A4 (panel w kroku „Generuj i kompiluj").
  const extraPageParcels = useMemo(
    () => parcelRows.map((r) => ({ id: r.parcel.id, label: r.parcel.short_number || r.parcel.teryt })),
    [parcelRows],
  );
  const { data: extraFilesData } = useWpwMassExtraFiles(ctx);
  const extraFilesCount = extraFilesData?.files.length ?? 0;
  const packageOrderLabel = extraFilesCount > 0
    ? `QR → formularze → ${extraFilesCount} dod. ${plural(extraFilesCount, "strona", "strony", "stron")} A4 → mapa → wypis → formularze → mapa`
    : "QR → formularze → mapa → wypis → formularze → mapa";
  // Nazwiska WSZYSTKICH właścicieli działki (krok 1 pokazuje je zbiorczo pod działką).
  const ownersByParcel = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const r of rows) {
      if (!r.owner) continue;
      const list = m.get(r.parcel.id) ?? [];
      if (!list.includes(r.owner.full_name)) list.push(r.owner.full_name);
      m.set(r.parcel.id, list);
    }
    return m;
  }, [rows]);
  // Zaznaczenie i krok kreatora są TRWAŁE na backendzie (szkic sekwencji, data.sequence)
  // — przeżywają F5 i zmianę urządzenia. sessionStorage zostaje jako natychmiastowy cache
  // dla trybu zlecenia (bez encji sekwencji) i jako fallback zanim dojedzie overview.
  const selKey = `wpw-mass-sel:${orderId || data?.connection_id || ""}`;
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!rows.length || restoredRef.current || (isSequence && !data)) return;
    restoredRef.current = true;
    const rowIds = new Set(parcelRows.map((r) => r.parcel.id));
    // 1) szkic sekwencji z backendu ma priorytet; 2) sessionStorage; 3) wszystko zaznaczone.
    const backendSel = isSequence && data?.sequence ? data.sequence.selected_parcels : null;
    const cached = sessionStorage.getItem(selKey);
    const source = backendSel ?? (cached ? (JSON.parse(cached) as string[]) : null);
    // Bez zapisanego wyboru: preselekcja tylko działek WYMAGAJĄCYCH akcji (access_mode ≠
    // „nie wymaga") i BEZ blokady (KW / zakres CAD / brak właściciela) — zablokowana
    // działka w kolejnych krokach dawała same błędy, choć nikt jej nie wybierał.
    // Dodatkowe (kontekst) zostają widoczne, ale niezaznaczone — user je dokłada.
    const actionIds = parcelRows
      .filter((r) => r.parcel.inclusion.requires_action && !parcelBlockReason(r))
      .map((r) => r.parcel.id);
    setSelected(source ? new Set(source.filter((id) => rowIds.has(id))) : new Set(actionIds));
    // Krok kreatora z backendu, o ile URL go nie narzuca (świeże wejście bez ?krok=).
    if (isSequence && data?.sequence?.step && !searchParams.get("krok")) {
      goStep(data.sequence.step as StepKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length, data?.sequence]);
  useEffect(() => {
    if (restoredRef.current) sessionStorage.setItem(selKey, JSON.stringify([...selected]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Zapis szkicu sekwencji na backend (debounce) — po każdej zmianie zaznaczenia/kroku.
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!restoredRef.current || !isSequence || !data?.can_mutate || !data?.connection_id) return;
    const connId = data.connection_id;
    const payload = { selected_parcel_ids: [...selected], step };
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveWpwSequence(connId, payload).catch(() => {/* zapis best-effort — nie blokuje UI */});
    }, 600);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, step, isSequence, data?.connection_id]);

  // Wiersze właścicieli zaznaczonych działek (liczniki WNU/pakietów — per WNU/właściciel).
  const selectedRows = useMemo(() => rows.filter((r) => selected.has(r.parcel.id)), [rows, selected]);
  // …a tabela kroków 3/4 pokazuje jeden wiersz na działkę — wiersze WNU są w `rowsByParcel`.
  const rowsByParcel = useMemo(() => groupRowsByParcel(rows), [rows]);
  // Wiersze są per (działka × właściciel) — liczniki „działek" liczą działki, nie wiersze.
  const parcelCountOf = (list: WpwMassRow[]) => new Set(list.map((r) => r.parcel.id)).size;
  const allParcelCount = useMemo(() => parcelCountOf(rows), [rows]);
  // Zaznaczone DZIAŁKI (unikalne) — do mutacji (parcel_ids) i liczników działek.
  const selectedParcelRows = useMemo(() => parcelRows.filter((r) => selected.has(r.parcel.id)), [parcelRows, selected]);
  const selectedIds = useMemo(() => selectedParcelRows.map((r) => r.parcel.id), [selectedParcelRows]);
  // Zaznaczone działki, którym brakuje opisu któregoś dobranego formularza.
  const missingOpisRows = useMemo(
    () => selectedParcelRows.filter((r) => opisStatusesOf(r).length > 0 && !opisReady(r)),
    [selectedParcelRows],
  );
  // Widok uwzględnienia (krok 1): działki wymagające akcji vs dodatkowe (kontekst). Backend
  // sortuje wymagające przed dodatkowymi, więc pierwsza dodatkowa działka dostaje separator.
  const actionParcelCount = useMemo(
    () => parcelRows.filter((r) => r.parcel.inclusion.requires_action).length, [parcelRows],
  );
  // Pozyskane (uzgodnione): bez wymaganej akcji, ale to nie „kontekst" —
  // liczone osobno, żeby podsumowanie nie wrzucało ich do „dodatkowych".
  const securedParcelCount = useMemo(
    () => parcelRows.filter((r) => r.parcel.inclusion.secured).length, [parcelRows],
  );
  // Skrót „odznacz pozyskane i dodatkowe": zbiór działek wymagających pozyskania i licznik
  // zaznaczonych spoza niego (pozyskane + dodatkowe) — gdy 0, przycisk nie ma czego robić.
  const actionParcelIds = useMemo(
    () => new Set(parcelRows.filter((r) => r.parcel.inclusion.requires_action).map((r) => r.parcel.id)),
    [parcelRows],
  );
  const selectedNonActionCount = useMemo(
    () => [...selected].filter((id) => !actionParcelIds.has(id)).length,
    [selected, actionParcelIds],
  );
  const additionalParcelCount = parcelRows.length - actionParcelCount - securedParcelCount;
  const firstAdditionalId = useMemo(
    () => parcelRows.find((r) => !r.parcel.inclusion.requires_action)?.parcel.id ?? null, [parcelRows],
  );
  // Działki z blokadą: w kroku 1 wyjaśnia, czemu preselekcja ich pominęła; w krokach
  // 3/4 ostrzega o zaznaczonych, których pakiet i tak nie powstanie bez ruchu człowieka.
  const blockedParcelCount = useMemo(
    () => parcelRows.filter((r) => r.parcel.inclusion.requires_action && parcelBlockReason(r)).length,
    [parcelRows],
  );
  const blockedSelected = useMemo(
    () => selectedParcelRows
      .map((r) => ({ row: r, reason: parcelBlockReason(r) }))
      .filter((x): x is { row: WpwMassRow; reason: string } => !!x.reason),
    [selectedParcelRows],
  );
  // Status WNU zaznaczonych działek (krok „WNU") — per właściciel.
  const rowsWithPkg = useMemo(() => selectedRows.filter((r) => r.application), [selectedRows]);
  const rowsWithoutPkg = useMemo(() => selectedRows.filter((r) => !r.application && r.owner), [selectedRows]);
  // Skompilowany = jest PDF pakietu (ścieżka standardowa). Kontrola AI z „Przygotuj
  // i sprawdź" (quality.ready) jest opcją — liczona osobno, tylko informacyjnie.
  const compiledCount = useMemo(
    () => selectedRows.filter((r) => r.compilations.length > 0 && !isPackageStale(r)).length,
    [selectedRows],
  );
  const verifiedCount = useMemo(() => selectedRows.filter((r) => r.quality?.ready).length, [selectedRows]);

  // ── krok 5: nowe zamówienie (domyślnie) albo zakończenie bez zamówienia ──
  // Sekwencja zakończona bez zamówienia (powrót z huba) otwiera się od razu na liście
  // zamówień do zmiany; przełącznik pozwala mimo to zamówić teraz.
  const navigate = useNavigate();
  const seqCompleted = isSequence && data?.sequence?.status === "completed";
  const [orderModeChoice, setOrderModeChoice] = useState<OrderMode | null>(null);
  const orderMode: OrderMode = orderModeChoice ?? (seqCompleted ? "finish" : "new");
  const finishSeq = useMutation({
    mutationFn: () => {
      if (!data?.connection_id) throw new Error("Brak projektu.");
      // Zapisuje ostatnie zaznaczenie razem z zakończeniem — bez czekania na autozapis.
      return finalizeWpwSequence(data.connection_id, { without_order: true, selected_parcel_ids: [...selected] });
    },
    onSuccess: () => {
      refetch();
      qc.invalidateQueries({ queryKey: ["pp", "wpw-sequences"] });
    },
  });
  const { data: projectOrders, isLoading: projectOrdersLoading } = useQuery({
    queryKey: ["orders", "wpw-change-targets", projectId],
    queryFn: () => ordersApi.list({ project: projectId }),
    enabled: isSequence && step === "zlecenie" && orderMode === "finish" && !!projectId,
  });
  const changeTargetOrders = useMemo(() => wpwChangeTargetOrders(projectOrders ?? []), [projectOrders]);
  // Właściciele ze skompilowanym, aktualnym pakietem — PODŚWIETLENIE na „Liście WPW do
  // pozyskania" w formularzu wniosku o zmianę wybranego zamówienia (location.state);
  // bez zaznaczania i bez ruszania ilości — decyzja należy do użytkownika.
  const compiledOwnerIds = useMemo(
    () => selectedRows
      .filter((r) => r.owner && r.compilations.length > 0 && !isPackageStale(r))
      .map((r) => r.owner!.id),
    [selectedRows],
  );

  // Widoczność kolumn per krok (w trybie sekwencji); tryb zlecenia pokazuje wszystko.
  const showContentCols = !isSequence || step === "tresc";
  const showBuildCols = !isSequence || step === "generuj";
  // Kroki „WNU" i „zlecenie" mają WŁASNE widoki (nie tabelę działek).
  const showTable = !isSequence || step === "dzialki" || step === "tresc" || step === "generuj";
  // Kolumna zaznaczania + PEŁNA lista tylko w kroku wyboru działek; dalej — tylko zaznaczone.
  const showSelectCol = !isSequence || step === "dzialki";
  // Krok 1: po jednym wierszu na DZIAŁKĘ (wybór działek). Kroki 3/4: per WŁAŚCICIEL.
  const [mailFilter, setMailFilter] = useState("");
  const tableRowsAll = showSelectCol ? parcelRows : selectedParcelRows;
  // Filtr ścieżki listownej działa po WIERSZACH WŁAŚCICIELI działki (działka zostaje, gdy
  // którykolwiek jej list pasuje) — tylko w krokach z kolumną „Generaty / pakiet".
  const tableRows = useMemo(() => {
    if (!showBuildCols || !mailFilter) return tableRowsAll;
    return tableRowsAll.filter((row) =>
      (rowsByParcel.get(row.parcel.id) ?? [row]).some((r) => matchesMailFilter(r, mailFilter)),
    );
  }, [tableRowsAll, showBuildCols, mailFilter, rowsByParcel]);
  // Kroki 3/4 (i tryb zlecenia): właściciele z WNU wypisani pod działką zamiast osobnych wierszy.
  const ownersListed = showContentCols || showBuildCols;
  const colCount = (showSelectCol ? 2 : 1) + (showContentCols ? 2 : 0) + (showBuildCols ? 2 : 0);

  // Rodzaje dokumentów WNU pod operatora (OSD) projektu: ENERGA — 3 formularze per status,
  // TAURON — jedno porozumienie TD.
  const kinds = wpwFormKindsFor(data?.operator);
  const missingTemplates = kinds.filter((k) => data && !data.templates[k]);
  // Jak nazywamy dokumenty formularzowe pakietu w UI danego operatora.
  const formsLabel = data?.operator === "tauron" ? "Porozumienia TD" : "Formularze ENERGA";
  const formsLabelLower = formsLabel.toLowerCase();
  const canMutate = !!data?.can_mutate;
  // zakładanie WNU ma luźniejszą bramkę niż reszta mutacji (nie wymaga can_submit)
  const canFound = !!data?.can_found_applications;
  const busy = !!task;

  // Bramka „Dalej": nie puszczaj do zlecenia, gdy nie ma z czego (brak mapy/wypisu →
  // brak skompilowanego pakietu). Każdy krok ma swój obiektywny warunek wejścia dalej.
  const readyCount = useMemo(
    () => selectedRows.filter((r) => r.map.ok && r.wypis.ok && opisReady(r)).length,
    [selectedRows],
  );
  const nextBlockedReason = useMemo(() => {
    if (step === "dzialki" && selectedIds.length === 0) return "Zaznacz działki, by przejść dalej";
    if (step === "pakiety" && rowsWithPkg.length === 0) return "Załóż WNU dla zaznaczonych działek";
    if (step === "generuj" && compiledCount === 0)
      return readyCount === 0
        ? "Brak gotowych działek — dobierz mapę i wypis (kliknij MAPA/WYPIS), potem skompiluj pakiet"
        : "Skompiluj co najmniej jeden pakiet, zanim przejdziesz do zlecenia";
    return "";
  }, [step, selectedIds.length, rowsWithPkg.length, compiledCount, readyCount]);

  const startTask = (label: string) => (result: { task_id: string }) =>
    setTask({ id: result.task_id, label, finished: false });
  // Po zadaniu masowym (zakończonym ALBO anulowanym w trakcie) — wszystko, co mogło się
  // zmienić poza overview.
  const refreshAfterTask = () => {
    m.invalidate();
    // Zadania zwrotów (segmentacja) zmieniają stan paczki/pozycji poza overview
    // — bez tego lista „Zwroty z terenu" nie odświeża się po analizie (F5).
    qc.invalidateQueries({ queryKey: ["pp", "wpw-return-batches"] });
    qc.invalidateQueries({ queryKey: ["pp", "wpw-return-batch"] });
    // Kompilacja zmienia listę pakietów WPW, a to ONA decyduje w kroku 5, kogo
    // zaznaczyć i kto ma komplet wejściowy. Bez tego powrót do kroku 5 w ciągu
    // staleTime czytał listę sprzed kompilacji.
    qc.invalidateQueries({ queryKey: ["pp", "wpw-packages"] });
    // Opisy AI zmieniają stan pól #…_prv_ai# w panelu „pola szablonów” —
    // bez tego licznik „do uzupełnienia” pokazywał je jako puste do F5.
    qc.invalidateQueries({ queryKey: ["pp", "wpw-mass-parcel-fields"] });
    refetch();
  };

  // Ostrzeżenia zaznaczonych działek (właściciele niezgodni z KW) — przed generacją,
  // kompilacją i „Przygotuj i sprawdź" wymagają JAWNEGO potwierdzenia w modalu.
  // Nie blokują: po „Rozumiem" zadanie startuje tak samo.
  // Ten sam modal pyta o POMIJALNE blokady (błąd analizy KW): po „pomiń błąd” najpierw
  // zdejmuje je na backendzie (zwolnienie per działka), dopiero potem startuje zadanie —
  // generacja czyta `kw_gate_override` z bazy, więc kolejność jest istotna.
  const [kwWarningPrompt, setKwWarningPrompt] = useState<{
    items: { row: WpwMassRow; reason: string }[];
    skippable: { row: WpwMassRow; reason: string }[];
    actionLabel: string;
    proceed: () => void;
  } | null>(null);
  const [kwSkipBusy, setKwSkipBusy] = useState(false);
  const [kwSkipError, setKwSkipError] = useState<string | null>(null);
  const withKwWarningConfirm = (actionLabel: string, proceed: () => void) => {
    const pick = (fn: (row: WpwMassRow) => string | null) => selectedParcelRows
      .map((row) => ({ row, reason: fn(row) }))
      .filter((x): x is { row: WpwMassRow; reason: string } => !!x.reason);
    const items = pick(parcelWarningReason);
    const skippable = pick(parcelSkippableBlockReason);
    if (items.length === 0 && skippable.length === 0) { proceed(); return; }
    setKwSkipError(null);
    setKwWarningPrompt({ items, skippable, actionLabel, proceed });
  };
  const confirmKwWarning = async (skipParcelIds: string[]) => {
    if (!kwWarningPrompt) return;
    const { proceed } = kwWarningPrompt;
    if (skipParcelIds.length > 0) {
      setKwSkipBusy(true);
      setKwSkipError(null);
      try {
        await Promise.all(skipParcelIds.map((parcelId) => m.kwGateOverride.mutateAsync({ parcelId, skip: true })));
      } catch (err) {
        setKwSkipError(extractApiError(err) || "Nie udało się pominąć błędu analizy KW.");
        return;
      } finally {
        setKwSkipBusy(false);
      }
    }
    setKwWarningPrompt(null);
    proceed();
  };

  // „Popraw ręcznie →" przy zablokowanej pozycji: dobierz krok po kodzie blokady,
  // dopilnuj zaznaczenia działki (kroki 3/4 pokazują tylko zaznaczone) i przewiń
  // do jej wiersza. Bez `parcel_id` (starsze wyniki) dopasowanie po TERYT.
  const locateBlocked = (item: TaskBlockedItem) => {
    const parcelId = item.parcel_id
      ?? rows.find((r) => r.parcel.teryt === item.teryt)?.parcel.id;
    if (!parcelId) return;
    if (!selected.has(parcelId)) setSelected((s) => new Set(s).add(parcelId));
    goStep(stepForBlocker(item.code));
    setHighlightParcel(parcelId);
  };
  useEffect(() => {
    if (!highlightParcel) return;
    const el = document.querySelector<HTMLElement>(`[data-parcel-id="${highlightParcel}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(() => setHighlightParcel(null), 4000);
    return () => clearTimeout(t);
  }, [highlightParcel, step]);

  // Zatwierdzanie zakresu z etykiet CAD: „w toku" i błąd per DZIAŁKA (useMutation pamięta
  // tylko ostatnie wywołanie, a zatwierdzać można kilka działek naraz).
  const scopePending = useScopeConfirmPendingIds();
  const scopeErrorFor = (parcelId: string): string | null => {
    if (m.confirmScope.isError && m.confirmScope.variables?.parcelId === parcelId) {
      return extractApiError(m.confirmScope.error) || "Nie udało się zatwierdzić zakresu.";
    }
    return m.confirmScopes.data?.failed.find((f) => f.parcelId === parcelId)?.error ?? null;
  };
  // Zaznaczone działki z wątpliwościami do zatwierdzenia; bez dobranego formularza backend
  // zatwierdzenia nie przyjmie (wymaga co najmniej jednego) — te trzeba najpierw zaznaczyć.
  const scopeNeededRows = useMemo(
    () => selectedParcelRows.filter((r) => r.parcel.scope_review?.needed),
    [selectedParcelRows],
  );
  const scopeConfirmable = useMemo(
    () => scopeNeededRows.filter((r) => r.effective_form_kinds.length > 0),
    [scopeNeededRows],
  );
  const confirmAllScopes = () => m.confirmScopes.mutate({
    items: scopeConfirmable.map((r) => ({ parcelId: r.parcel.id, form_kinds: r.effective_form_kinds })),
  });

  const toggle = (parcelId: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(parcelId)) next.delete(parcelId);
      else next.add(parcelId);
      return next;
    });

  const toggleKind = (row: WpwMassRow, kind: WpwEnergaFormKind) => {
    const current = new Set(row.effective_form_kinds);
    if (current.has(kind)) current.delete(kind);
    else current.add(kind);
    m.patchParcel.mutate({ parcelId: row.parcel.id, form_kinds: kinds.filter((k) => current.has(k)) });
  };

  const generateRowAi = (row: WpwMassRow) => {
    // force=true przelicza WSZYSTKIE kubełki — także wpisane ręcznie. Decyzja użytkownika
    // (09.09.2026): wolno, ale ma to być powiedziane wprost przed kliknięciem.
    const manual = Object.entries(row.parcel.consent_description_sources ?? {})
      .filter(([status, src]) => src === "manual" && (row.parcel.consent_work_descriptions?.[status] || "").trim())
      .map(([status]) => status);
    if (manual.length > 0 && !window.confirm(
      `Działka ${row.parcel.short_number || row.parcel.teryt} ma opisy wpisane RĘCZNIE (${manual.join(", ")}). `
      + "AI ułoży je od nowa i NADPISZE ręczną treść. Kontynuować?",
    )) return;
    setRowAiParcel(row.parcel.id);
    m.describe.mutate(
      { parcelIds: [row.parcel.id], force: true },
      {
        onSuccess: startTask(`Opis AI — dz. ${row.parcel.teryt}`),
        onSettled: () => setRowAiParcel(null),
      },
    );
  };

  // Ręczna korekta formularza: wgrany ODT → nowa wersja (backend konwertuje do PDF).
  const handleOdtUpload = async (file: File) => {
    if (!odtTarget) return;
    setOdtBusy(true);
    setOdtMsg(null);
    try {
      const res = await replaceWpwFormOdt(odtTarget, file);
      // Konwersja ODT→PDF potrafi paść — wtedy powstaje wersja BEZ PDF-a, a kompilacja
      // pakietu jest zablokowana (nie schodzi po cichu do poprzedniej wersji).
      setOdtMsg(res.status === "success"
        ? { kind: "ok", text: `Wgrano poprawiony formularz jako wersję v${res.version} — skompiluj pakiet ponownie.` }
        : { kind: "error", text: `Wersja v${res.version} powstała, ale nie ma PDF-a. ${res.error || ""} Możesz też zapisać poprawiony plik jako PDF i wgrać gotowy PDF — wejdzie do pakietu bez konwersji.` });
      refetch();
    } catch (e) {
      const detail = (e as { response?: { data?: { errors?: { detail?: unknown } } } })
        ?.response?.data?.errors?.detail;
      setOdtMsg({ kind: "error", text: typeof detail === "string" ? detail : "Wgranie poprawionego ODT nie powiodło się." });
    } finally {
      setOdtBusy(false);
      setOdtTarget(null);
      if (odtRef.current) odtRef.current.value = "";
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={
          isSequence
            ? "Sekwencja masowa WPW — przygotowanie i zlecenie"
            : `Masowe pakiety WPW (${OPERATOR_LABEL[data?.operator ?? ""] ?? "ENERGA"}) — ${data?.order?.number ?? "…"}`
        }
        subtitle={
          !data ? undefined
            : isSequence
              ? `${data.project_name} · działek prywatnych: ${allParcelCount} (WNU/właścicieli: ${rows.length}) · krok po kroku: wnioski → opisy → formularze → kompilacja → utwórz zlecenie`
              : `Zamówienie ${data.order?.number} · ${data.order?.contractor ?? "bez wykonawcy"} · działek: ${allParcelCount} (WNU/właścicieli: ${rows.length})`
        }
        backTo={`/power-projects/${projectId}${isSequence ? "?tab=wpw" : ""}`}
        backLabel={isSequence ? "Zakładka WPW" : "Projekt"}
      />

      {/* Ukryty input do re-uploadu poprawionego formularza (krok 4). Edytowalne źródło
          (ODT/DOCX/DOC/RTF) konwertuje backend; PDF wchodzi wprost — to wyjście awaryjne,
          gdy edytor zapisze plik, którego LibreOffice nie otwiera. */}
      <input ref={odtRef} type="file" className="hidden"
             accept=".odt,.docx,.doc,.rtf,.pdf,application/vnd.oasis.opendocument.text,application/pdf"
             onChange={(e) => e.target.files?.[0] && handleOdtUpload(e.target.files[0])} />
      {odtMsg && (
        <p className={`text-xs rounded-lg px-3 py-2 border flex items-start gap-2 ${
          odtMsg.kind === "ok"
            ? "text-green-800 dark:text-green-300 border-green-300 dark:border-green-500/40 bg-green-50/70 dark:bg-green-500/10"
            : "text-red-800 dark:text-red-300 border-red-300 dark:border-red-500/40 bg-red-50/70 dark:bg-red-500/10"}`}>
          <span className="flex-1">{odtMsg.text}</span>
          <button type="button" className="shrink-0 hover:underline" onClick={() => setOdtMsg(null)}>zamknij</button>
        </p>
      )}

      {isLoading && <div className="py-10 flex justify-center"><Spinner /></div>}

      {data && (
        <>
          {missingTemplates.length > 0 && (
            <p className="text-xs text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-500/40 bg-amber-50/70 dark:bg-amber-500/10 rounded-lg px-3 py-2 flex items-center gap-2">
              <AlertTriangle size={14} className="shrink-0" />
              <span>
                Brak szablonów {(OPERATOR_LABEL[data.operator] ?? data.operator).toUpperCase()}:{" "}
                <b>{missingTemplates.map((k) => WPW_ENERGA_KIND_SHORT[k]).join(", ")}</b> — wgraj je
                w Generacji dokumentów (rodzaj{" "}
                {missingTemplates.map((k) => `„${DOC_TEMPLATE_KIND_LABELS[k]}"`).join(", ")},
                operator {data.operator}).
              </span>
            </p>
          )}

          {/* Pełnomocnictwo zlecenia — TAURON drukuje jego numer na porozumieniu i wystawia
              pełnomocnictwo osobno na każde zlecenie. Panel mówi, jaki numer wejdzie na
              dokumenty, albo czego brakuje — zanim ktoś kliknie „generuj". */}
          {data.poa && (
            data.poa.ok ? (
              <p className="text-xs text-[var(--color-text-secondary)] border border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)] rounded-lg px-3 py-2 flex items-center gap-2">
                <ShieldCheck size={14} className="shrink-0" />
                <span>
                  Pełnomocnictwo zlecenia: <b className="font-mono">nr {data.poa.number}</b>
                  {" "}· {data.poa.attorney}
                  {" "}· {data.poa.valid_until ? `ważne do ${fmtDate(data.poa.valid_until)}` : "bezterminowe"}
                  {data.poa.validity === "expiring" && " (wygasa wkrótce)"}
                </span>
              </p>
            ) : (
              <p className="text-xs text-red-800 dark:text-red-300 border border-red-300 dark:border-red-500/40 bg-red-50/70 dark:bg-red-500/10 rounded-lg px-3 py-2 flex items-start gap-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>
                  {data.poa.reason}{" "}
                  <Link to="/power-projects/pelnomocnictwa" className="underline">
                    Rejestr pełnomocnictw
                  </Link>
                </span>
              </p>
            )
          )}

          {/* Rysunek wgrany do wariantu POBOCZNEGO → WPW/AI czytają ubogą wersję
              aktualną. Bez tego „0 obiektów / brak geometrii" wygląda jak awaria mimo
              poprawnego uploadu. Bursztynowy, nie czerwony: nic nie blokuje generowania,
              a przy różnicy paru obiektów to zwykle fałszywy alarm — decyzję, który
              rysunek jest właściwy, podejmuje człowiek. Nazewnictwo jak w zakładce Mapa
              („wariant obowiązujący", „wersja aktualna", „bieżąca w wariancie"). */}
          {data.variant_warning && (
            <div className="text-xs text-amber-900 dark:text-amber-200 border border-amber-300 dark:border-amber-500/40 bg-amber-50/70 dark:bg-amber-500/10 rounded-lg px-3 py-2 flex items-start gap-2">
              <AlertTriangle size={14} className="shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
              <div className="space-y-0.5">
                <p className="font-medium">
                  {data.variant_warning.binding.version ? (
                    <>
                      Wersja aktualna {data.variant_warning.binding.version} ma mniej
                      {" "}obiektów niż wersja bieżąca w wariancie
                      {" "}{data.variant_warning.richer.variant}
                      {" "}({data.variant_warning.binding.objects} vs
                      {" "}{data.variant_warning.richer.objects}).
                    </>
                  ) : (
                    <>
                      Wariant obowiązujący {data.variant_warning.binding.variant} nie ma
                      {" "}wersji trasy, a wariant {data.variant_warning.richer.variant} ma
                      {" "}{data.variant_warning.richer.objects} obiektów.
                    </>
                  )}
                </p>
                <p className="text-amber-800/90 dark:text-amber-200/80">
                  {data.variant_warning.reason}
                </p>
              </div>
            </div>
          )}

          {/* ── kreator: nagłówek ze stepperem + operatorem + intro kroku ── */}
          {isSequence && (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] shadow-sm overflow-hidden">
              <div className="px-4 pt-4 pb-3 border-b border-[var(--color-border-muted)]">
                <StepperHeader step={step} onGo={goStep} />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-[var(--color-bg-elevated)]">
                <div>
                  <div className="text-sm font-semibold text-[var(--color-text-primary)]">
                    {stepIdx + 1}. {STEPS[stepIdx].label}
                  </div>
                  <div className="text-[11px] text-[var(--color-text-muted)] mt-0.5">
                    {step === "dzialki" && (
                      <>
                        {!canMutate && (
                          <span className="text-red-700 dark:text-red-300 font-medium">
                            Tylko podgląd — nie masz uprawnień do pracy w tym projekcie, zaznaczenie i akcje są wyłączone.{" "}
                          </span>
                        )}
                        Zaznacz działki prywatne, które wchodzą do tej sekwencji.{" "}
                        <b>{actionParcelCount}</b> wymaga pozyskania (preselekcja)
                        {blockedParcelCount > 0 && (
                          <>, w tym <b>{blockedParcelCount}</b> z blokadą — niezaznaczone automatycznie
                            (powód przy działce; zaznaczysz je ręcznie po jej zdjęciu)</>
                        )}
                        {securedParcelCount > 0 && <> · <b>{securedParcelCount}</b> pozyskanych (uzgodnione — bez preselekcji)</>}
                        {additionalParcelCount > 0 && <> · <b>{additionalParcelCount}</b> dodatkowych (kontekst)</>}.
                      </>
                    )}
                    {step === "pakiety" && "Załóż WNU dla każdego właściciela działek, które go jeszcze nie mają (działka z kilkoma współwłaścicielami = osobny WNU na każdego). „Pakiet” to dopiero skompilowany PDF — powstaje w kroku 4. Tu też decydujesz, kto faktycznie jedzie w teren: przy drodze wewnętrznej (wg księgi wieczystej) zwykle wystarczą zgody właścicieli reprezentujących ponad połowę udziałów."}
                    {step === "tresc" && "Sprawdź dobór formularzy i uzupełnij opisy urządzeń (AI lub ręcznie)."}
                    {step === "generuj" && `„Generuj i kompiluj” tworzy ${formsLabelLower} i od razu składa z nich pakiety. „Kompiluj pakiety” składa je ponownie bez generacji — po zmianie mapy, wypisu albo wgraniu poprawionego ODT.`}
                    {step === "zlecenie" && "Utwórz zlecenie WPW — skompilowane pakiety podepną się automatycznie."}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-mono text-[var(--color-text-muted)]">
                    zaznaczone {selectedIds.length}/{allParcelCount} {plural(allParcelCount, "działki", "działek", "działek")}
                  </span>
                  {/* Cała sekwencja jednym kliknięciem. Endpoint istniał od dawna, ale
                      w aplikacji NIE MIAŁ ani jednego wywołania — jedynym jego
                      użytkownikiem był ARON, więc ścieżka nigdy nie przeszła przez
                      ludzkie ręce i nikt nie zauważył, że nie robi planu sytuacyjnego
                      (EOP 64, 31.08–01.09.2026). Teraz robi — i ma tu swój przycisk. */}
                  {isSequence && (
                    <TaskBusyHint task={task}>
                    <Button size="sm" variant="secondary"
                      disabled={!canFound || !canMutate || busy || selectedIds.length === 0}
                      loading={m.prepareAll.isPending}
                      onClick={() => withKwWarningConfirm("przygotuj i sprawdź", () => m.prepareAll.mutate(selectedIds, {
                        // Nazwa przebiegu = nazwa przycisku; „Sekwencja masowa WPW" sugerowała inne działki/projekt.
                        onSuccess: startTask("Przygotuj i sprawdź"),
                      }))}
                      title={"OPCJA — cała sekwencja naraz z kontrolą AI; ten sam przycisk działa identycznie na każdym kroku: "
                        + "przygotowuje mapy, czyta etykiety DXF, ogląda mapę, dobiera formularze, poprawia opisy (ręcznie "
                        + "wpisanych nie zmienia), generuje, kompiluje i sprawdza każdą stronę pakietu. Kroki 2–5 działają "
                        + "też bez tego przycisku. Wznawia niedokończone etapy, gotowe pakiety wykorzystuje bez zmian."}>
                      <Sparkles size={13} className="mr-1" /> Przygotuj i sprawdź ({selectedIds.length}) · opcja
                    </Button>
                    </TaskBusyHint>
                  )}
                  {step === "tresc" && (
                    <TaskBusyHint task={task}>
                    <Button size="sm" disabled={!canMutate || busy || selectedIds.length === 0}
                      onClick={() => m.describe.mutate({ parcelIds: selectedIds }, { onSuccess: startTask("Opisy urządzeń (AI)") })}
                      title="AI ułoży opis urządzeń per działka z elementów mapy — dolicza tylko brakujące kubełki, ręcznych wpisów nie nadpisuje">
                      <Sparkles size={13} className="mr-1" /> Generuj opisy (AI)
                    </Button>
                    </TaskBusyHint>
                  )}
                  {step === "tresc" && scopeNeededRows.length > 0 && (
                    <TaskBusyHint task={task}>
                    <Button size="sm" variant="secondary"
                      disabled={!canMutate || busy || scopeConfirmable.length === 0}
                      loading={m.confirmScopes.isPending}
                      onClick={confirmAllScopes}
                      data-testid="confirm-all-scopes"
                      title={scopeConfirmable.length === 0
                        ? "Działki z wątpliwościami nie mają dobranego żadnego formularza — zaznacz formularze w ich wierszach"
                        : `Zatwierdź dobrane formularze jako zakres na ${scopeConfirmable.length} ${plural(scopeConfirmable.length, "działce", "działkach", "działkach")} z wątpliwościami z etykiet CAD`
                          + (scopeNeededRows.length > scopeConfirmable.length
                            ? ` (${scopeNeededRows.length - scopeConfirmable.length} bez dobranego formularza pomijam)` : "")}>
                      <ShieldCheck size={13} className="mr-1" /> Zatwierdź zakres wszystkich ({scopeConfirmable.length})
                    </Button>
                    </TaskBusyHint>
                  )}
                  {step === "generuj" && (
                    <>
                      {/* Druk dwustronny dotyczy obu przycisków — generacja też składa pakiety. */}
                      <label className="inline-flex items-center gap-1.5 text-[11px] text-[var(--color-text-secondary)] cursor-pointer select-none"
                        title="Wstawia pustą stronę za okładką QR (jej rewers), a przy nieparzystej liczbie stron formularzy także przed załącznikami — formularze i załączniki zaczynają się na nowych kartkach">
                        <input type="checkbox" checked={duplexPrint} onChange={(e) => setDuplexPrint(e.target.checked)} />
                        przygotuj pod wydruk dwustronny
                      </label>
                      <TaskBusyHint task={task}>
                      <Button size="sm" disabled={!canMutate || busy || selectedIds.length === 0}
                        onClick={() => withKwWarningConfirm("generuj i kompiluj", () => m.generateForms.mutate({ parcelIds: selectedIds, duplex: duplexPrint }, { onSuccess: startTask(`${formsLabel} i pakiety`) }))}
                        title={`Wygeneruj dobrane ${formsLabelLower} (ODT → PDF przez LibreOffice; każda generacja to nowa wersja) `
                          + `i od razu złóż pakiety: ${packageOrderLabel}. WNU bez mapy, wypisu albo z polami do uzupełnienia `
                          + "dostaje formularze, a pakiet czeka — powód na liście pod logiem"}>
                        Generuj i kompiluj
                      </Button>
                      </TaskBusyHint>
                      <TaskBusyHint task={task}>
                      <Button size="sm" variant="secondary"
                        disabled={!canMutate || busy || readyCount === 0}
                        onClick={() => withKwWarningConfirm("kompiluj pakiety", () => m.compile.mutate({ parcelIds: selectedIds, duplex: duplexPrint }, { onSuccess: startTask("Kompilacja pakietów") }))}
                        title={readyCount === 0
                          ? "Brak gotowych działek — najpierw dobierz mapę i wypis (kliknij MAPA/WYPIS w kolumnie Gotowość)"
                          : `Złóż pakiety ponownie BEZ generowania formularzy — po zmianie mapy, wypisu, druku dwustronnego, `
                            + `stron A4 albo po wgraniu poprawionego ODT. ${readyCount} gotowych WNU `
                            + `(${parcelCountOf(selectedRows.filter((r) => r.map.ok && r.wypis.ok && opisReady(r)))} działek): ${packageOrderLabel} (niegotowe pomija)`}>
                        Kompiluj pakiety{readyCount > 0 && selectedRows.length !== readyCount ? ` (${readyCount}/${selectedRows.length} WNU)` : ""}
                      </Button>
                      </TaskBusyHint>
                    </>
                  )}
                </div>
              </div>
              {/* Zaznaczone działki z blokadą (KW / zakres CAD / brak właściciela) — ich pakiet
                  nie powstanie, a błąd wychodził dopiero przy wierszu albo w logu zadania. */}
              {(step === "tresc" || step === "generuj") && blockedSelected.length > 0 && (
                <p className="px-4 py-2 text-[11px] leading-snug text-amber-700 dark:text-amber-400 border-t border-[var(--color-border-muted)]"
                   data-testid="blocked-selected-notice">
                  ⚠ {blockedSelected.length === 1 ? "1 zaznaczona działka ma" : `${blockedSelected.length} zaznaczonych działek ma`} blokadę:{" "}
                  {blockedSelected.map(({ row, reason }) => `${row.parcel.short_number || row.parcel.teryt} (${reason})`).join("; ")}.
                  Odznacz je w kroku „Działki” albo zdejmij blokadę (analiza KW — zakładka KW projektu,
                  zakres — „Zatwierdź zakres” w kroku „Formularze i opisy”, właściciel — dane działki).
                  {blockedSelected.some(({ row }) => parcelSkippableBlockReason(row)) && (
                    <> Błąd analizy KW (brak właścicieli z Działu II) możesz pominąć z potwierdzeniem —
                    kreator zapyta po kliknięciu „Generuj” / „Kompiluj”.</>
                  )}
                </p>
              )}
              {/* Działki bez kompletu opisów — generacja JE BLOKUJE (validate_form_values:
                  opis to wymagane pole druku; decyzja 09.09.2026), reszta zaznaczenia
                  przechodzi. Ma być widoczne PRZED kliknięciem, nie dopiero w błędzie zadania. */}
              {step === "generuj" && missingOpisRows.length > 0 && (
                <p className="px-4 py-2 text-[11px] leading-snug text-red-700 dark:text-red-300 border-t border-[var(--color-border-muted)]">
                  ⛔ Bez opisu urządzeń: {[...new Set(missingOpisRows.map((r) => r.parcel.short_number || r.parcel.teryt))].join(", ")}.
                  Formularze tych działek NIE wygenerują się (opis to wymagane pole druku) — reszta
                  zaznaczenia przejdzie, a te działki wrócą na liście „Do poprawienia ręcznie”.
                  Wróć do kroku „Formularze i opisy”, żeby uzupełnić opisy.
                </p>
              )}
              {/* operator (OSD) — wykryty automatycznie, pod niego dobrane szablony */}
              <div className="px-4 py-2 text-[11px] text-[var(--color-text-secondary)] border-t border-[var(--color-border-muted)] flex items-center gap-1.5">
                <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-bg-base)] border border-[var(--color-border)] px-2 py-0.5 font-mono uppercase tracking-wide">
                  {OPERATOR_LABEL[data.operator] ?? data.operator}
                </span>
                <span className="text-[var(--color-text-muted)]">
                  operator klienta{data.client_name && ` „${data.client_name}"`} — formularze WPW dobrane pod niego
                </span>
              </div>
            </div>
          )}

          {/* ── krok „Generuj i kompiluj": dodatkowe strony A4 przed formatkami A3 ── */}
          {isSequence && step === "generuj" && (
            <ExtraPagesPanel ctx={ctx} parcels={extraPageParcels} canMutate={canMutate} />
          )}

          {/* ── krok „WNU": zwięzły status zaznaczonych działek (nie pełna lista) ── */}
          {isSequence && step === "pakiety" && (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] shadow-sm overflow-hidden">
              {/* pasek podsumowania + akcja */}
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-[var(--color-border-muted)]">
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-[var(--color-text-primary)]">
                    <b>{selectedIds.length}</b> {plural(selectedIds.length, "działka", "działki", "działek")}
                  </span>
                  <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400"
                    title="Właściciele (WNU) na zaznaczonych działkach, którzy mają już założony wniosek">
                    <CheckCircle2 size={14} /> <b>{rowsWithPkg.length}</b> WNU założonych
                  </span>
                  {rowsWithoutPkg.length > 0
                    ? <span className="text-amber-700 dark:text-amber-400" title="Właściciele bez wniosku WNU"><b>{rowsWithoutPkg.length}</b> WNU do założenia</span>
                    : <span className="text-[var(--color-text-muted)]">0 do założenia</span>}
                </div>
                {rowsWithoutPkg.length > 0 ? (
                  <TaskBusyHint task={task}>
                  <Button size="sm" disabled={!canFound || busy}
                    loading={m.ensureApplications.isPending}
                    onClick={() => m.ensureApplications.mutate([...new Set(rowsWithoutPkg.map((r) => r.parcel.id))])}
                    title="Załóż WNU dla każdego właściciela działek, które go jeszcze nie mają">
                    Załóż {rowsWithoutPkg.length} WNU
                  </Button>
                  </TaskBusyHint>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-[11px] text-green-700 dark:text-green-400">
                    <CheckCircle2 size={14} /> wszystkie mają WNU — dalej
                  </span>
                )}
              </div>

              {/* Zakres zgód per DZIAŁKA: kto podpisuje, z jakim udziałem i kto faktycznie
                  jedzie w teren; do tego bramka analizy KW i — przy drodze wewnętrznej wg
                  księgi — podpowiedź większości udziałów zamiast kompletu zgód. */}
              <div className="max-h-[32rem] overflow-y-auto">
                {selectedParcelRows.map((r) => (
                  <ConsentScopeCard
                    key={r.parcel.id}
                    parcel={r.parcel}
                    disabled={!canMutate || m.fieldExclusion.isPending || m.kwGateOverride.isPending}
                    onOpenKw={() => setKwParcel({
                      id: r.parcel.id, teryt: r.parcel.teryt, kw_number: r.parcel.kw_number,
                    })}
                    onExclude={(owners, excluded, reason) =>
                      m.fieldExclusion.mutate({ parcelId: r.parcel.id, owners, excluded, reason })}
                    onOverride={(reason) => m.kwGateOverride.mutate({ parcelId: r.parcel.id, reason })}
                    onClearOverride={() => m.kwGateOverride.mutate({ parcelId: r.parcel.id, clear: true })}
                    onSkip={() => m.kwGateOverride.mutate({ parcelId: r.parcel.id, skip: true })}
                  />
                ))}
                {selectedParcelRows.length === 0 && (
                  <div className="px-4 py-6 text-center text-[11px] text-[var(--color-text-muted)]">
                    Wróć do kroku „Działki" i zaznacz działki.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── pasek akcji (tryb istniejącego zlecenia — pełny) ── */}
          {!isSequence && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-mono uppercase tracking-wide text-[var(--color-text-muted)]">
              Zaznaczone: {selectedIds.length}/{parcelRows.length}
            </span>
            <TaskBusyHint task={task}>
            <Button
              size="sm" variant="secondary" disabled={!canFound || busy || selectedIds.length === 0}
              loading={m.ensureApplications.isPending}
              onClick={() => m.ensureApplications.mutate(selectedIds)}
              title="Załóż wniosek „Pakiet WPW” dla każdego właściciela zaznaczonych działek bez wniosku"
            >
              1. Utwórz pakiety (wnioski)
            </Button>
            </TaskBusyHint>
            <TaskBusyHint task={task}>
            <Button
              size="sm" variant="secondary" disabled={!canMutate || busy || selectedIds.length === 0}
              onClick={() => m.describe.mutate({ parcelIds: selectedIds }, { onSuccess: startTask("Opisy urządzeń (AI)") })}
              title="AI ułoży opis urządzeń per działka z elementów mapy — dolicza tylko brakujące kubełki, ręcznych wpisów nie nadpisuje"
            >
              2. Generuj opisy (AI)
            </Button>
            </TaskBusyHint>
            {/* Druk dwustronny dotyczy obu przycisków — generacja też składa pakiety. */}
            <label className="inline-flex items-center gap-1.5 text-[11px] text-[var(--color-text-secondary)] cursor-pointer select-none"
              title="Wstawia pustą stronę za okładką QR (jej rewers), a przy nieparzystej liczbie stron formularzy także przed załącznikami — formularze i załączniki zaczynają się na nowych kartkach">
              <input type="checkbox" checked={duplexPrint} onChange={(e) => setDuplexPrint(e.target.checked)} />
              przygotuj pod wydruk dwustronny
            </label>
            <TaskBusyHint task={task}>
            <Button
              size="sm" variant="secondary" disabled={!canMutate || busy || selectedIds.length === 0}
              onClick={() => withKwWarningConfirm("generuj i kompiluj", () => m.generateForms.mutate({ parcelIds: selectedIds, duplex: duplexPrint }, { onSuccess: startTask(`${formsLabel} i pakiety`) }))}
              title={`Wygeneruj dobrane ${formsLabelLower} (ODT → PDF przez LibreOffice; każda generacja to nowa wersja) `
                + `i od razu złóż pakiety: ${packageOrderLabel} (2. egzemplarz zostaje u WPW)`}
            >
              3. Generuj i kompiluj
            </Button>
            </TaskBusyHint>
            <TaskBusyHint task={task}>
            <Button
              size="sm" variant="secondary" disabled={!canMutate || busy || selectedIds.length === 0}
              onClick={() => withKwWarningConfirm("kompiluj pakiety", () => m.compile.mutate({ parcelIds: selectedIds, duplex: duplexPrint }, { onSuccess: startTask("Kompilacja pakietów") }))}
              title={`Złóż pakiety ponownie BEZ generowania formularzy — po zmianie mapy, wypisu, druku dwustronnego `
                + `albo po wgraniu poprawionego ODT: ${packageOrderLabel} (2. egzemplarz zostaje u WPW)`}
            >
              Kompiluj pakiety
            </Button>
            </TaskBusyHint>
            <TaskBusyHint task={task}>
            <Button
              size="sm" disabled={!data.can_handoff || busy || selectedIds.length === 0}
              onClick={() => m.handoff.mutate(selectedIds, { onSuccess: startTask("Przekazanie negocjatorowi") })}
              title="Przekaż skompilowane pakiety negocjatorowi (gk_docs przekazań WPW) — kierownik/dyrektor"
            >
              4. Przekaż negocjatorowi
            </Button>
            </TaskBusyHint>
          </div>
          )}
          {!isSequence && <ExtraPagesPanel ctx={ctx} parcels={extraPageParcels} canMutate={canMutate} />}

          {kwWarningPrompt && (
            <KwWarningConfirmModal
              items={kwWarningPrompt.items}
              skippable={kwWarningPrompt.skippable}
              actionLabel={kwWarningPrompt.actionLabel}
              busy={kwSkipBusy}
              error={kwSkipError}
              onCancel={() => setKwWarningPrompt(null)}
              onConfirm={confirmKwWarning}
            />
          )}
          {task && (
            <TaskLogPanel
              taskId={task.id}
              label={task.label}
              onClose={(cancelled) => {
                setTask(null);
                if (cancelled) refreshAfterTask();
              }}
              onLocate={locateBlocked}
              onDone={(status, partial) => {
                refreshAfterTask();
                // Zakończony przebieg dalej blokuje przyciski — podpowiedź na nich mówi
                // wtedy „zamknij panel", nie „trwa zadanie".
                setTask((t) => (t ? { ...t, finished: true } : t));
                // Po błędzie i po „sukcesie z pominięciami" panel zostaje z listą działek
                // do poprawienia — chował się po 4 s razem z powodem, zanim ktokolwiek
                // zdążył go przeczytać.
                if (status === "success" && !partial) setTimeout(() => setTask(null), 4000);
              }}
            />
          )}

          {/* ── tabela działek (kroki 1/3/4 — pełna przy wyborze, tylko zaznaczone dalej) ── */}
          {showTable && showBuildCols && (
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              <Mail size={12} className="text-[var(--color-text-muted)]" />
              <select className="input-base text-[11px] py-1" value={mailFilter} onChange={(e) => setMailFilter(e.target.value)}
                      data-testid="mail-filter" aria-label="Filtr ścieżki listownej">
                {MAIL_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
              {mailFilter && (
                <span className="text-[var(--color-text-muted)]">{tableRows.length} z {tableRowsAll.length} działek</span>
              )}
            </div>
          )}
          {showTable && (
          <div className="overflow-x-auto rounded-xl border border-[var(--color-border)] shadow-sm bg-[var(--color-bg-surface)]">
            <table className="w-full text-xs align-top">
              <thead>
                <tr className="text-left border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)] font-medium uppercase tracking-wide text-[10px] text-[var(--color-text-muted)]">
                  {showSelectCol && (
                    <th className="px-3 py-2 w-8">
                      <input
                        type="checkbox"
                        disabled={!canMutate}
                        title={canMutate ? undefined : "Tylko podgląd — brak uprawnień do pracy w tym projekcie"}
                        checked={tableRows.length > 0 && tableRows.every((r) => selected.has(r.parcel.id))}
                        onChange={(e) =>
                          setSelected(e.target.checked ? new Set(tableRows.map((r) => r.parcel.id)) : new Set())
                        }
                      />
                    </th>
                  )}
                  <th className="px-3 py-2">Działka i właściciele</th>
                  {showContentCols && <th className="px-3 py-2 w-44 max-w-[11rem]">{formsLabel}</th>}
                  {showContentCols && <th className="px-3 py-2 w-[46%] min-w-[520px]">Opis urządzeń</th>}
                  {showBuildCols && <th className="px-3 py-2">Gotowość</th>}
                  {showBuildCols && <th className="px-3 py-2">Generaty / pakiet</th>}
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row) => {
                  // Jedna działka = jeden wiersz; `group` to jej wiersze właścicieli (po jednym
                  // WNU) — generaty/pakiet i pola szablonów pokazujemy per WNU wewnątrz wiersza.
                  const group = rowsByParcel.get(row.parcel.id) ?? [row];
                  const ownerCount = group.filter((r) => r.owner).length;
                  const hasApplication = group.some((r) => r.application);
                  const blockedAll = blockedAcrossOwners(group);
                  const isBlocked = blockedAll.length > 0;
                  // Powód, dla którego preselekcja kroku 1 pominęła działkę (bramka KW /
                  // zakres CAD / brak właściciela) — oznaczamy go wprost przy wierszu.
                  const blockReason = parcelBlockReason(row);
                  const rowKey = row.parcel.id;
                  const fieldsRow = fieldsParcel?.startsWith(`${rowKey}:`)
                    ? group.find((r) => fieldsKeyOf(r) === fieldsParcel) : undefined;
                  return (
                    <Fragment key={rowKey}>
                    {showSelectCol && row.parcel.id === firstAdditionalId && (
                      <tr className="border-t border-[var(--color-border)]">
                        <td colSpan={colCount} className="px-3 py-1.5 bg-[var(--color-bg-elevated)] text-[10px] font-mono uppercase tracking-wide text-[var(--color-text-muted)]">
                          <div className="flex items-center justify-between gap-2">
                            <span>Działki bez wymaganego pozyskania — pozyskane (uzgodnione) i dodatkowe (kontekst); domyślnie niezaznaczone</span>
                            {/* Szkic sekwencji sprzed uzgodnienia działek może je trzymać
                                zaznaczone — tu odznacza się je jednym kliknięciem. */}
                            {canMutate && selectedNonActionCount > 0 && (
                              <button
                                type="button"
                                className="shrink-0 normal-case font-sans text-[11px] font-medium text-[var(--color-primary)] hover:underline"
                                title="Odznacz działki pozyskane (uzgodnione) i dodatkowe (kontekst) — zostaną tylko wymagające pozyskania"
                                onClick={() =>
                                  setSelected((prev) => new Set([...prev].filter((id) => actionParcelIds.has(id))))
                                }
                              >
                                odznacz zaznaczone z tej sekcji ({selectedNonActionCount})
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    <tr data-parcel-id={row.parcel.id}
                      className={`border-t border-[var(--color-border-muted)] align-top hover:bg-[var(--color-bg-elevated)]/50 transition-colors${
                      showSelectCol && !row.parcel.inclusion.requires_action ? " opacity-70" : ""}${
                      highlightParcel === row.parcel.id ? " bg-amber-100 dark:bg-amber-500/20 ring-2 ring-inset ring-amber-400" : ""}`}>
                      {showSelectCol && (
                      <td className="px-3 py-2.5">
                        <input
                          type="checkbox"
                          disabled={!canMutate}
                          title={canMutate ? undefined : "Tylko podgląd — brak uprawnień do pracy w tym projekcie"}
                          checked={selected.has(row.parcel.id)}
                          onChange={() => toggle(row.parcel.id)}
                        />
                      </td>
                      )}
                      <td className="px-3 py-2.5">
                        <div className="font-mono text-[11px] font-semibold text-[var(--color-text-primary)]">{row.parcel.teryt}</div>
                        <div className="text-[var(--color-text-secondary)]">
                          {row.parcel.short_number && <>dz. {row.parcel.short_number} · </>}
                          {row.parcel.obreb || row.parcel.commune}
                        </div>
                        {showSelectCol && (
                          <div className="mt-0.5 flex flex-wrap items-center gap-1">
                            <span className="inline-flex items-center rounded px-1 py-px text-[9px] font-mono uppercase tracking-wide bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] border border-[var(--color-border-muted)]">
                              {row.parcel.inclusion.kind_label}
                            </span>
                            {/* nasz ruch / złożony wniosek (czekamy na urząd) / pozyskana / kontekst */}
                            <span
                              className={`inline-flex items-center rounded px-1 py-px text-[9px] font-mono uppercase tracking-wide ${
                                row.parcel.inclusion.state === "action"
                                  ? "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300"
                                  : row.parcel.inclusion.state === "awaiting_response"
                                    ? "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300"
                                    : row.parcel.inclusion.state === "kpa_overdue"
                                      ? "bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300"
                                    : row.parcel.inclusion.state === "secured"
                                      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300"
                                      : "bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] border border-[var(--color-border-muted)]"
                              }`}
                              title={row.parcel.inclusion.access_mode_label}
                            >
                              {row.parcel.inclusion.state === "action"
                                ? "wymaga pozyskania"
                                : row.parcel.inclusion.state === "awaiting_response"
                                  ? "oczekuje na odpowiedź"
                                  : row.parcel.inclusion.state === "kpa_overdue"
                                    ? "po terminie KPA"
                                  : row.parcel.inclusion.state === "secured"
                                    ? "pozyskana"
                                    : "dodatkowa"}
                            </span>
                            {/* charakter działki WYŁĄCZNIE z księgi — także wtedy, gdy
                                księgi nie ma („brak danych KW") */}
                            <RoadBadge road={row.parcel.consent.road} />
                            {blockReason && (
                              <span
                                className="inline-flex items-center rounded px-1 py-px text-[9px] font-mono uppercase tracking-wide bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300"
                                title={blockReason}
                              >
                                blokada
                              </span>
                            )}
                          </div>
                        )}
                        {showSelectCol && blockReason && (
                          <div className="mt-0.5 text-[10px] leading-snug text-red-700 dark:text-red-400">
                            {blockReason} — dlatego bez preselekcji; zaznaczysz ją po zdjęciu blokady.
                          </div>
                        )}
                        <div className="text-[11px]">
                          {ownersListed ? (
                            // kroki 3/4 i tryb zlecenia: wszyscy właściciele działki, każdy ze swoim WNU
                            <ParcelOwnersList group={group} />
                          ) : (
                            // krok 1: wszyscy właściciele działki (każdy = osobny WNU)
                            <span className="text-[var(--color-text-muted)]">
                              {(ownersByParcel.get(row.parcel.id) ?? []).join(", ") || "brak właściciela"}
                              {(ownersByParcel.get(row.parcel.id)?.length ?? 0) > 1 &&
                                <span className="text-[9px] text-amber-700"> · {ownersByParcel.get(row.parcel.id)!.length} WNU</span>}
                              {/* zgód bywa mniej niż WNU — małżeństwo podpisuje raz */}
                              {row.parcel.consent.unit_count > 1 &&
                                row.parcel.consent.unit_count !== (ownersByParcel.get(row.parcel.id)?.length ?? 0) && (
                                <span className="text-[9px] text-[var(--color-text-muted)]">
                                  {" "}· {row.parcel.consent.unit_count} zgód
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                        {/* krok 1: WNU reprezentanta działki (w krokach 3/4 każdy WNU jest na liście wyżej) */}
                        {!ownersListed && (row.application ? (
                          <Link
                            to={`/power-projects/wnioski/${row.application.id}`}
                            className="text-[11px] font-mono text-[var(--color-primary)] hover:underline"
                            title="Otwórz WNU"
                          >
                            {row.application.number}
                          </Link>
                        ) : (
                          <span className="text-[11px] text-amber-700 dark:text-amber-400">bez WNU</span>
                        ))}
                        {!ownersListed && row.application?.status === "return_received" && (
                          <span className="ml-1 text-[10px] font-mono uppercase text-green-700">zwrot ✓</span>
                        )}
                        {!ownersListed && row.field_excluded && (
                          <span className="ml-1 text-[10px] font-mono uppercase text-[var(--color-text-muted)]"
                            title={row.field_excluded_reason || "Pominięty świadomie w kroku „WNU”"}>
                            nie idzie w teren
                          </span>
                        )}
                        {/* Bramka analizy KW i przypomnienie o dopytaniu ZE — widoczne już
                            przy wyborze działek, żeby nie wyszło dopiero przed drukiem. */}
                        {showSelectCol && row.parcel.consent.kw_gate.blocker && (
                          <p className="mt-1 text-[10px] leading-snug text-amber-700 dark:text-amber-400">
                            {row.parcel.consent.kw_gate.blocker.reason}
                          </p>
                        )}
                        {/* Rozbieżność z KW: ostrzeżenie (nie blokuje) — potwierdzane w modalu
                            przed generacją/kompilacją. */}
                        {showSelectCol && parcelWarningReason(row) && (
                          <p className="mt-1 text-[10px] leading-snug text-amber-700 dark:text-amber-400">
                            ⚠ ostrzeżenie: {parcelWarningReason(row)}
                          </p>
                        )}
                        {showSelectCol && row.parcel.consent.alert && (
                          <p className="mt-1 text-[10px] leading-snug text-amber-700 dark:text-amber-400">
                            {row.parcel.consent.alert.text}
                          </p>
                        )}
                        {/* Wątpliwości analizy etykiet CAD — sygnał już przy wyborze działek;
                            szczegóły i zatwierdzenie w kroku „Formularze i opisy”. */}
                        {showSelectCol && row.parcel.scope_review?.needed && (
                          <p className="mt-1 text-[10px] leading-snug text-amber-700 dark:text-amber-400"
                            title={row.parcel.scope_review.doubts.map((d) => d.reason).join("\n")}>
                            ⚠ zakres z etykiet CAD do zatwierdzenia ({row.parcel.scope_review.doubts.length})
                          </p>
                        )}
                      </td>
                      {showContentCols && (
                      <td className="px-3 py-2.5 w-44 max-w-[11rem]">
                        <div className="space-y-0.5">
                          {kinds.map((kind) => {
                            const on = row.effective_form_kinds.includes(kind);
                            // Pusty dobór ręczny = backend wraca do doboru z mapy, więc odznaczenie
                            // OSTATNIEGO formularza nie daje „żadnego" — mówimy to, zanim ktoś kliknie.
                            const last = on && row.effective_form_kinds.length === 1;
                            return (
                            <label key={kind} className="flex items-center gap-1.5 text-[11px] cursor-pointer select-none"
                              title={last ? "Odznaczenie ostatniego formularza przywraca dobór automatyczny z mapy" : undefined}>
                              <input
                                type="checkbox"
                                // Blokada tylko na czas zapisu TEJ działki — cudzy zapis (opis w innym
                                // wierszu) nie ma zamrażać wszystkich checkboxów w tabeli.
                                disabled={!canMutate || !hasApplication
                                  || (m.patchParcel.isPending && m.patchParcel.variables?.parcelId === row.parcel.id)}
                                checked={on}
                                onChange={() => toggleKind(row, kind)}
                              />
                              {WPW_ENERGA_KIND_SHORT[kind]}
                              {row.auto_form_kinds.includes(kind) && row.form_kinds.length === 0 && (
                                <span className="text-[9px] font-mono uppercase text-[var(--color-text-muted)]">auto</span>
                              )}
                            </label>
                            );
                          })}
                          {row.form_kinds.length > 0 && (
                            <span className="text-[9px] font-mono uppercase text-blue-600">dobór ręczny</span>
                          )}
                          {row.breakdown.approximate && (
                            <p className="text-[10px] text-amber-700">klasyfikacja przybliżona (brak geometrii)</p>
                          )}
                          {(row.breakdown.label_statuses?.length ?? 0) > 0 && (
                            <p className="text-[10px] text-[var(--color-text-muted)]"
                              title={(row.breakdown.labels ?? []).filter((l) => l.inside).map((l) => `„${l.text}”`).join(", ")}>
                              z etykiet CAD: {row.breakdown.label_statuses.join(", ")}
                            </p>
                          )}
                          <ScopeReviewNotice
                            review={row.parcel.scope_review}
                            effectiveKinds={row.effective_form_kinds}
                            disabled={!canMutate || !hasApplication}
                            pending={scopePending.has(row.parcel.id)}
                            error={scopeErrorFor(row.parcel.id)}
                            onConfirm={(kinds) => m.confirmScope.mutate({ parcelId: row.parcel.id, confirm: true, form_kinds: kinds })}
                            onClear={() => m.confirmScope.mutate({ parcelId: row.parcel.id, confirm: false })}
                          />
                        </div>
                      </td>
                      )}
                      {showContentCols && (
                      <td className="px-3 py-2.5">
                        <RowDescriptionEditor
                          row={row}
                          disabled={!canMutate}
                          generating={rowAiParcel === row.parcel.id}
                          onSave={(consent) =>
                            m.patchParcel.mutateAsync({ parcelId: row.parcel.id, consent_work_descriptions: consent })
                          }
                          onGenerate={() => generateRowAi(row)}
                          aside={group.filter((r) => r.application).map((r) => {
                            // pola szablonów są per WNU — przy kilku współwłaścicielach jeden
                            // przełącznik na każdego (z nazwiskiem)
                            const key = fieldsKeyOf(r);
                            return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => setFieldsParcel((p) => (p === key ? null : key))}
                              className="inline-flex items-center gap-0.5 text-[10px] font-mono uppercase tracking-wide text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                              title="Sprawdź i uzupełnij pozostałe #zmienne# szablonów PRZED generacją (co jest puste i gdzie to zapisać)"
                            >
                              <ChevronDown size={11}
                                className={`transition-transform ${fieldsParcel === key ? "rotate-180" : ""}`} />
                              pola szablonów{ownerCount > 1 && r.owner ? ` · ${r.owner.full_name}` : ""}
                              {/* WNU po zwrocie (return_received/completed) nie jest już aktywnym
                                  pakietem — endpoint pól odpowiada 400; nie pytamy. */}
                              <ParcelFieldsCount parcelId={row.parcel.id} owner={r.owner?.id}
                                enabled={step === "tresc" && !["return_received", "completed"].includes(r.application!.status)} />
                            </button>
                            );
                          })}
                        />
                      </td>
                      )}
                      {showBuildCols && (
                      <td className="px-3 py-2.5">
                        <div className="flex flex-col gap-1 items-start">
                          <button type="button" disabled={!canMutate}
                            onClick={() => setMapPicker((p) => (p === rowKey ? null : rowKey))}
                            className="disabled:cursor-default" title="Dobierz / zmień mapę (formatkę) — wspólna dla działki">
                            <ReadyChip ok={row.map.ok} label="mapa" reason={row.map.reason || row.map.label} />
                          </button>
                          <button type="button" disabled={!canMutate}
                            onClick={() => setWypisPicker((p) => (p === rowKey ? null : rowKey))}
                            className="disabled:cursor-default" title="Dobierz / wgraj wypis i przypisz strony — wspólny dla działki">
                            <ReadyChip
                              ok={row.wypis.ok} label="wypis"
                              reason={row.wypis.reason || (row.wypis.pages ? `str. ${row.wypis.pages.join(",")}` : "")}
                            />
                          </button>
                          <ReadyChip
                            ok={opisReady(row)}
                            label="opis"
                            reason={
                              opisReady(row)
                                ? opisStatusesOf(row)
                                    .map((s) => `${s}: ${row.parcel.consent_work_descriptions?.[s] || ""}`)
                                    .join("\n")
                                : "Brak opisu urządzeń dla dobranych formularzy (per status)"
                            }
                          />
                        </div>
                        {canMutate && (
                          <p className="mt-1 text-[9px] text-[var(--color-text-muted)]">kliknij mapę/wypis, aby dobrać</p>
                        )}
                        {isBlocked && (
                          <ul className="mt-1 space-y-0.5">
                            {blockedAll.map((b, i) => (
                              <li key={`${b.code}-${i}`} className="text-[10px] text-red-600 flex flex-col gap-1">
                                <span className="flex items-start gap-1">
                                  <XCircle size={10} className="mt-0.5 shrink-0" />
                                  <span>
                                    {b.partial && <span className="font-medium">{b.owners.join(", ")}: </span>}
                                    {b.reason}
                                  </span>
                                </span>
                                {b.code === "map_stale" && canMutate && (
                                  <>
                                    <Button size="sm" variant="secondary"
                                      loading={m.acceptStaleMap.isPending && m.acceptStaleMap.variables === row.parcel.id}
                                      onClick={() => m.acceptStaleMap.mutate(row.parcel.id)}
                                      className="self-start mt-0.5"
                                      title="Użyj tej nieaktualnej mapy mimo to — przestanie blokować gotowość">
                                      Użyj mapy mimo to
                                    </Button>
                                    {m.acceptStaleMap.isError && m.acceptStaleMap.variables === row.parcel.id && (
                                      <span className="text-[10px] text-red-600">
                                        {(m.acceptStaleMap.error as { response?: { data?: { errors?: { detail?: string } } } })
                                          ?.response?.data?.errors?.detail || "Nie udało się zaakceptować mapy."}
                                      </span>
                                    )}
                                  </>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                      )}
                      {showBuildCols && (
                      <td className="px-3 py-2.5">
                        {/* generaty i pakiet są per WNU — przy kilku współwłaścicielach blok na każdego */}
                        <div className="space-y-2">
                          {group.map((r) => (
                          <div key={r.owner?.id ?? "no-owner"} className="space-y-0.5" data-testid="owner-build">
                            {ownerCount > 1 && (
                              <p className="text-[10px] text-[var(--color-text-secondary)]">
                                <span className="font-medium">{r.owner?.full_name ?? "brak właściciela"}</span>
                                {r.application && <> · <span className="font-mono">{r.application.number}</span></>}
                              </p>
                            )}
                          {kinds.filter((k) => row.effective_form_kinds.includes(k)).map((kind) => {
                            const newest = r.form_documents.find((d) => d.kind === kind);
                            const ok = newest?.status === "success";
                            return (
                              <div key={kind} className="flex items-center gap-1 text-[11px] flex-wrap">
                                <FileText size={11} className="shrink-0 text-[var(--color-text-muted)]" />
                                {ok && newest?.file?.url ? (
                                  <a href={newest.file.url} target="_blank" rel="noreferrer"
                                     onClick={(e) => { if (!isPlainLeftClick(e)) return; e.preventDefault(); void openFile(newest.file!); }}
                                     className="underline decoration-dotted">
                                    {WPW_ENERGA_KIND_SHORT[kind]} v{newest.version}
                                  </a>
                                ) : newest ? (
                                  <span className="text-red-700 dark:text-red-400" title={newest.error}
                                        >{WPW_ENERGA_KIND_SHORT[kind]} v{newest.version} — tylko ODT, konwersja padła (blokuje kompilację)</span>
                                ) : (
                                  <span className="text-[var(--color-text-muted)]">{WPW_ENERGA_KIND_SHORT[kind]} —</span>
                                )}
                                {/* Domyślna droga poprawek: edytor w przeglądarce (nowa wersja po zapisie,
                                    PDF po zapisie). Pobierz/wgraj zostaje jako wyjście awaryjne. */}
                                {newest?.odt_file && canMutate && editorAvailable && (
                                  <button type="button"
                                    onClick={() => setEditorForm({ id: newest.id, label: `${WPW_ENERGA_KIND_SHORT[kind]} — ${row.parcel.teryt}${ownerCount > 1 && r.owner ? ` · ${r.owner.full_name}` : ""}` })}
                                    className={`text-[10px] font-medium hover:underline ${(newest.unfilled_fields?.length ?? 0) > 0 ? "text-amber-700 dark:text-amber-400" : "text-[var(--color-primary)]"}`}
                                    title="Otwórz formularz w edytorze w przeglądarce — uzupełnij pola ręczne i zapisz jako nową wersję">edytuj</button>
                                )}
                                {newest?.odt_file?.url && (
                                  <a href={newest.odt_file.url} download
                                     onClick={(e) => { if (!isPlainLeftClick(e)) return; e.preventDefault(); void openFile(newest.odt_file!); }}
                                     className="text-[10px] text-[var(--color-text-secondary)] hover:underline" title="Pobierz ODT do ręcznej korekty (wyjście awaryjne)">ODT↓</a>
                                )}
                                {newest && canMutate && (
                                  <button type="button" disabled={odtBusy}
                                    onClick={() => { setOdtTarget(newest.id); odtRef.current?.click(); }}
                                    className="text-[10px] text-[var(--color-primary)] hover:underline disabled:opacity-50"
                                    title="Wgraj poprawiony formularz jako nową wersję — ODT/DOCX/DOC/RTF (przerobimy na PDF) albo gotowy PDF (wyjście awaryjne)">popraw↑</button>
                                )}
                                {(newest?.unfilled_fields?.length ?? 0) > 0 && (
                                  <span className="basis-full text-[10px] text-amber-700 dark:text-amber-400"
                                        title="Znaczniki #wpisz_…# wciąż w formularzu — kompilacja pakietu czeka, aż zostaną uzupełnione w edytorze">
                                    ⚠ do uzupełnienia: {newest!.unfilled_fields!.map((n) => `#${n}#`).join(", ")}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                          {/* Własny plik wgrany przy pojedynczym WNU wchodzi do pakietu ZAMIAST
                              formularzy — bez tej informacji lista formularzy powyżej kłamałaby
                              o zawartości pakietu. */}
                          {r.own_document && (
                            <div className="flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-400">
                              <FileText size={11} className="shrink-0" />
                              <span title="Usuń własny plik na stronie WNU, jeśli pakiet ma zawierać formularze">
                                własny plik „{r.own_document.original_filename}” zastępuje formularze
                              </span>
                            </div>
                          )}
                          {r.compilations[0]?.file?.url ? (
                            <div className="flex items-center gap-1 text-[11px] flex-wrap">
                              <Download size={11} className="shrink-0 text-green-700" />
                              <a href={r.compilations[0].file.url} download
                                 onClick={(e) => { if (!isPlainLeftClick(e)) return; e.preventDefault(); void openFile(r.compilations[0].file!); }}
                                 className="underline decoration-dotted font-medium">
                                Pakiet v{r.compilations[0].version}
                              </a>
                              <span className="text-[10px] text-[var(--color-text-muted)]">
                                {fmtDate(r.compilations[0].created_at)}
                                {r.compilations.length > 1 && ` (+${r.compilations.length - 1} starszych)`}
                              </span>
                              {/* Kontrola AI jest OPCJĄ: pakiet bez niej jest pełnoprawny
                                  (zlecenie, przekazanie). Odznaka tylko informuje. */}
                              <span className={r.quality?.ready ? "text-green-700" : "text-[var(--color-text-muted)]"}
                                    title={r.quality?.reason}>
                                {r.quality?.ready ? "✓ sprawdzony (AI)" : "niesprawdzony — opcjonalnie „Przygotuj i sprawdź”"}
                              </span>
                              {/* Pakiet złożony PRZED wgraniem poprawionego formularza ma starą
                                  treść — bez tego znacznika ludzie pobierali go dalej z listy. */}
                              {isPackageStale(r) && (
                                <span className="text-[10px] text-amber-700 dark:text-amber-400 font-medium"
                                      title="Formularz zmienił się po tej kompilacji — złóż pakiet ponownie przyciskiem „Kompiluj pakiety”.">
                                  ⚠ nieaktualny — przekompiluj
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-[11px] text-[var(--color-text-muted)]">bez kompilacji</span>
                          )}
                          <MailPackageCell
                            mail={r.mail_package}
                            onEditPart={canMutate && editorAvailable ? (part) => setEditorForm({
                              kind: part.editor_kind,
                              id: part.editor_id,
                              label: `${part.part === "cover" ? "pismo przewodnie listu" : `${part.label} w liście`} — ${row.parcel.teryt}${r.owner ? ` · ${r.owner.full_name}` : ""}`,
                            }) : undefined}
                          />
                          </div>
                          ))}
                        </div>
                      </td>
                      )}
                    </tr>
                    {/* krok 3: sprawdź/uzupełnij wszystkie pozostałe klucze szablonów PRZED generacją */}
                    {showContentCols && fieldsRow && (
                      <tr className="border-t border-dashed border-[var(--color-border-muted)] bg-[var(--color-bg-base)]">
                        <td />
                        <td colSpan={colCount - 1} className="px-2 pb-2">
                          {ownerCount > 1 && (
                            <p className="text-[10px] font-mono uppercase tracking-wide text-[var(--color-text-muted)] pt-1">
                              Pola szablonów · {fieldsRow.owner?.full_name}
                              {fieldsRow.application && ` · ${fieldsRow.application.number}`}
                            </p>
                          )}
                          <ParcelFieldsPanel parcelId={row.parcel.id} owner={fieldsRow.owner?.id} connectionId={data.connection_id} canMutate={canMutate} />
                        </td>
                      </tr>
                    )}
                    {/* krok 4: inline dobór mapy */}
                    {showBuildCols && mapPicker === rowKey && (
                      <tr className="border-t border-dashed border-[var(--color-border-muted)] bg-[var(--color-bg-base)]">
                        <td />
                        <td colSpan={colCount - 1} className="px-2 pb-2">
                          <p className="text-[10px] font-mono uppercase tracking-wide text-[var(--color-text-muted)] pt-1">Dobór mapy</p>
                          <MapPickerPanel parcelId={row.parcel.id} projectId={isSequence ? projectId : undefined}
                            onDone={() => setMapPicker(null)} />
                        </td>
                      </tr>
                    )}
                    {/* krok 4: inline dobór wypisu */}
                    {showBuildCols && wypisPicker === rowKey && (
                      <tr className="border-t border-dashed border-[var(--color-border-muted)] bg-[var(--color-bg-base)]">
                        <td />
                        <td colSpan={colCount - 1} className="px-2 pb-2">
                          <p className="text-[10px] font-mono uppercase tracking-wide text-[var(--color-text-muted)] pt-1">Dobór wypisu</p>
                          <WypisPickerPanel parcelId={row.parcel.id}
                            onDone={() => setWypisPicker(null)} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
                {tableRows.length === 0 && (
                  <tr>
                    <td colSpan={colCount} className="px-3 py-8 text-center text-[var(--color-text-muted)]">
                      {isSequence
                        ? (step === "dzialki" ? "Brak działek prywatnych w projekcie." : "Brak zaznaczonych działek — wróć do kroku Działki.")
                        : "Zamówienie nie ma przypiętych działek."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          )}

          {/* ── nawigacja kreatora: Wstecz / Dalej ── */}
          {isSequence && step !== "zlecenie" && (
            <div className="flex items-center justify-between">
              <Button size="sm" variant="ghost" disabled={stepIdx === 0}
                onClick={() => goStep(STEPS[Math.max(0, stepIdx - 1)].key)}>
                <ArrowLeft size={14} className="mr-1" /> Wstecz
              </Button>
              <Button size="sm"
                disabled={!!nextBlockedReason}
                onClick={() => goStep(STEPS[Math.min(STEPS.length - 1, stepIdx + 1)].key)}
                title={nextBlockedReason || undefined}>
                Dalej <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
          )}

          {/* ── krok 5: zlecenie — nowe zamówienie (domyślnie) albo zakończenie bez zamówienia ── */}
          {isSequence && step === "zlecenie" && (
            <SectionCard title="Krok 5 — zlecenie">
              <p className="text-[11px] text-[var(--color-text-muted)] mb-3">
                Skompilowanych pakietów (per właściciel): <b>{compiledCount}</b>
                {verifiedCount > 0 && <> (w tym sprawdzonych AI: {verifiedCount})</>} · zaznaczonych
                działek: {selectedIds.length}.
              </p>

              {/* Przełącznik: co dalej z pakietami. Domyślnie nowe zamówienie (jak dotąd). */}
              <div role="radiogroup" aria-label="Co dalej z pakietami" className="grid gap-2 sm:grid-cols-2 mb-4">
                {([
                  {
                    key: "new" as OrderMode, label: "Nowe zamówienie",
                    hint: "Wybierz podwykonawcę i pozycje — skompilowane pakiety podepną się do zlecenia jako komplety wejściowe automatycznie.",
                  },
                  {
                    key: "finish" as OrderMode, label: "Zakończ bez zamówienia",
                    hint: "Pakiety WPW zostają zapisane. Zamówisz później (sekwencja czeka w hubie Wnioski → WPW) albo dołączysz pakiety do istniejącego zamówienia WPW wnioskiem o zmianę.",
                  },
                ]).map((opt) => (
                  <label
                    key={opt.key}
                    className={`flex items-start gap-2 border px-3 py-2 cursor-pointer transition-colors ${
                      orderMode === opt.key
                        ? "border-[var(--color-primary)] bg-[var(--color-bg-elevated)]"
                        : "border-[var(--color-border)] hover:border-[var(--color-primary)]"
                    }`}
                  >
                    <input
                      type="radio" name="wpw-order-mode" value={opt.key} className="mt-0.5"
                      checked={orderMode === opt.key}
                      onChange={() => setOrderModeChoice(opt.key)}
                      data-testid={`order-mode-${opt.key}`}
                    />
                    <span className="text-xs">
                      <span className="font-medium block">{opt.label}</span>
                      <span className="text-[11px] text-[var(--color-text-muted)]">{opt.hint}</span>
                    </span>
                  </label>
                ))}
              </div>

              {orderMode === "new" && (
                <>
                  {seqCompleted && (
                    <p className="text-[11px] text-amber-700 dark:text-amber-400 mb-2">
                      Sekwencja została zakończona bez zamówienia {fmtDate(data.sequence?.completed_at)} —
                      utworzenie zamówienia domknie ją teraz.
                    </p>
                  )}
                  <WpwRequisitionModal
                    inline
                    connectionId={data.connection_id}
                    projectId={projectId}
                    // Tylko działki ZAZNACZONE w kroku 1 — cała lista projektu podsuwała w kroku 5
                    // właścicieli działek, których nikt nie wybrał, i błąd „Brak kompletu wejściowego”.
                    parcels={(seqParcels ?? []).filter((p) => selected.has(p.id))}
                    onCreated={(oid) => finalizeWpwSequence(data.connection_id, { order: oid }).catch(() => {})}
                    onClose={() => refetch()}
                  />
                </>
              )}

              {orderMode === "finish" && !seqCompleted && (
                <div className="border border-[var(--color-border)] px-3 py-3 space-y-2" data-testid="finish-without-order">
                  <p className="text-xs">
                    Zakończenie zapisuje sekwencję z zaznaczeniem ({selectedIds.length}{" "}
                    {plural(selectedIds.length, "działka", "działki", "działek")}) i skompilowanymi
                    pakietami ({compiledCount}). Potem pokażemy zamówienia WPW projektu, do których
                    możesz dołączyć pakiety wnioskiem o zmianę — albo wrócisz tu później i zamówisz.
                  </p>
                  {finishSeq.isError && (
                    <p className="text-xs text-red-600">
                      {extractApiError(finishSeq.error) || "Nie udało się zakończyć sekwencji."}
                    </p>
                  )}
                  <Button
                    size="sm" disabled={!canMutate} loading={finishSeq.isPending}
                    onClick={() => finishSeq.mutate()} data-testid="finish-without-order-btn"
                  >
                    <CheckCircle2 size={14} className="mr-1" /> Zakończ sekwencję bez zamówienia
                  </Button>
                </div>
              )}

              {orderMode === "finish" && seqCompleted && (
                <div className="space-y-3">
                  <div
                    className="border border-emerald-300 dark:border-emerald-700 bg-emerald-50/50 dark:bg-emerald-900/10 px-3 py-2 text-xs"
                    data-testid="sequence-completed-notice"
                  >
                    <CheckCircle2 size={14} className="inline mr-1 text-emerald-600 dark:text-emerald-400" />
                    Sekwencja zakończona bez zamówienia {fmtDate(data.sequence?.completed_at)}. Pakiety WPW
                    ({compiledCount}) są zapisane — wrócisz tu z huba Wnioski → WPW („Sekwencje masowe WPW
                    w toku”), żeby zamówić później, albo dołącz je do zamówienia poniżej.
                  </div>
                  <div>
                    <p className="text-xs font-medium mb-1">
                      Zamówienia WPW projektu — dołącz pakiety wnioskiem o zmianę
                    </p>
                    <p className="text-[11px] text-[var(--color-text-muted)] mb-2">
                      Wybór zamówienia otwiera jego formularz wniosku o zmianę; na „Liście WPW do pozyskania”
                      właściciele ze skompilowanym pakietem ({compiledOwnerIds.length}) będą podświetleni — sam
                      zdecydujesz: zamiana pakietu (ilość bez zmian), nowy WPW (zwiększ ilość) albo dwa za jeden (zmniejsz).
                    </p>
                    {projectOrdersLoading ? (
                      <Spinner />
                    ) : changeTargetOrders.length === 0 ? (
                      <p className="text-[11px] text-[var(--color-text-muted)]" data-testid="change-orders-empty">
                        Projekt nie ma otwartych zamówień WPW — gdy zechcesz zamówić, przełącz wyżej na „Nowe zamówienie”.
                      </p>
                    ) : (
                      <div className="border border-[var(--color-border)] overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-[var(--color-bg-elevated)] text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
                            <tr>
                              <th className="px-2 py-1.5 text-left">Numer</th>
                              <th className="px-2 py-1.5 text-left">Podwykonawca</th>
                              <th className="px-2 py-1.5 text-left">Status</th>
                              <th className="px-2 py-1.5 text-left">Termin</th>
                              <th className="px-2 py-1.5" />
                            </tr>
                          </thead>
                          <tbody>
                            {changeTargetOrders.map((o) => (
                              <tr key={o.id} className="border-t border-[var(--color-border-muted)]" data-testid={`change-order-${o.id}`}>
                                <td className="px-2 py-1.5 font-mono">
                                  <Link to={`/orders/${o.id}`} className="hover:underline">{o.order_number}</Link>
                                </td>
                                <td className="px-2 py-1.5">{o.contractor_name}</td>
                                <td className="px-2 py-1.5">{ORDER_STATUS_LABELS[o.status] ?? o.status}</td>
                                <td className="px-2 py-1.5 whitespace-nowrap">{fmtDate(o.deadline)}</td>
                                <td className="px-2 py-1.5 text-right">
                                  <Button
                                    size="sm"
                                    onClick={() => navigate(`/orders/${o.id}?wniosek=zmiana`, {
                                      state: { wpwPrefillOwners: compiledOwnerIds },
                                    })}
                                  >
                                    Wniosek o zmianę <ArrowRight size={14} className="ml-1" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-3">
                <Button size="sm" variant="ghost" onClick={() => goStep("generuj")}>
                  <ArrowLeft size={14} className="mr-1" /> Wstecz
                </Button>
              </div>
            </SectionCard>
          )}

          {/* ── zwroty z terenu (tylko dla istniejącego zlecenia) ── */}
          {!isSequence && (
          <SectionCard title="Zwroty z terenu">
            <p className="text-[11px] text-[var(--color-text-muted)] mb-2">
              Zbiorczy skan podpisanych pakietów → cięcie po QR okładek + AI (dopasowanie i skan
              dopisków/warunków) → przegląd → zastosowanie (wniosek + przekazania WPW).
            </p>
            <div className="flex items-center gap-2 mb-2">
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  setReturnUploadError("");
                  if (file) {
                    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
                      setReturnUploadError(`„${file.name}” nie jest plikiem PDF — zwrot z terenu musi być skanem PDF.`);
                    } else {
                      rm.upload.mutate({ orderId, file }, {
                        onError: (err) => setReturnUploadError(extractApiError(err) || "Nie udało się wgrać skanu zwrotu."),
                      });
                    }
                  }
                  e.target.value = "";
                }}
              />
              <Button
                size="sm" variant="secondary" disabled={!canMutate}
                loading={rm.upload.isPending}
                onClick={() => fileRef.current?.click()}
              >
                Wgraj skan zwrotu (PDF)
              </Button>
              {returnUploadError && (
                <span className="text-[11px] text-red-700 dark:text-red-300">{returnUploadError}</span>
              )}
            </div>
            <div className="space-y-1">
              {(batches ?? []).map((batch) => (
                <div
                  key={batch.id}
                  className="flex flex-wrap items-center gap-2 border border-[var(--color-border-muted)] px-2 py-1.5 text-[11px]"
                >
                  <ScrollText size={12} className="text-[var(--color-text-muted)]" />
                  <span className="font-mono">{batch.file?.original_filename ?? batch.id.slice(0, 8)}</span>
                  <span className="text-[var(--color-text-muted)]">
                    {batch.page_count ?? "?"} str. · {fmtDate(batch.created_at)} · pozycji: {batch.items_count}
                  </span>
                  <span className="font-mono uppercase text-[10px]">{RETURN_STATUS_LABEL[batch.status]}</span>
                  {batch.ai_cost_usd && (
                    <span className="text-[10px] text-[var(--color-text-muted)]">AI: {batch.ai_cost_usd} USD</span>
                  )}
                  <span className="ml-auto flex items-center gap-1.5">
                    {["uploaded", "proposed"].includes(batch.status) && canMutate && (
                      <Button
                        size="sm" variant="secondary" disabled={busy}
                        onClick={() =>
                          rm.segment.mutate(batch.id, {
                            onSuccess: startTask(`Analiza zwrotu (${batch.file?.original_filename ?? ""})`),
                          })
                        }
                      >
                        {batch.status === "proposed" ? "Analizuj ponownie" : "Analizuj (QR + AI)"}
                      </Button>
                    )}
                    {["proposed", "applied"].includes(batch.status) && (
                      <Button size="sm" onClick={() => setReviewBatch(batch.id)}>
                        Przegląd pozycji
                      </Button>
                    )}
                    {batch.status !== "applied" && batch.status !== "cancelled" && canMutate && (
                      <Button size="sm" variant="ghost" onClick={() => rm.cancel.mutate(batch.id)}>
                        Anuluj
                      </Button>
                    )}
                  </span>
                </div>
              ))}
              {(batches ?? []).length === 0 && (
                <p className="text-[11px] text-[var(--color-text-muted)]">Brak wgranych zwrotów.</p>
              )}
            </div>
          </SectionCard>
          )}

          {editorForm && (
        <OdtEditorScreen
          kind={editorForm.kind ?? "wpw_form"}
          id={editorForm.id}
          title={editorForm.kind ? `Edycja listu — ${editorForm.label}` : `Edycja formularza — ${editorForm.label}`}
          onClose={() => {
            setEditorForm(null);
            refetch();
          }}
        />
      )}
      {reviewBatch && (
            <FieldReturnReviewModal
              batchId={reviewBatch}
              rows={rows}
              canMutate={canMutate}
              onClose={() => {
                setReviewBatch(null);
                refetch();
              }}
            />
          )}

          {/* Księga wieczysta wprost z bramki KW — to samo okno co w zakładce Działki.
              Po zamknięciu odświeżamy overview, bo analiza mogła właśnie zdjąć blokadę. */}
          {kwParcel && (
            <KwAnalysisModal
              parcel={kwParcel}
              onClose={() => {
                setKwParcel(null);
                m.invalidate();
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
